import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/safeSupabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { TeamManagementContent } from '@/pages/TeamManagementPage';
import { BranchActionDialog, type BranchActionMode } from '@/components/org/BranchDialogs';
import { useHierarchyTree } from '@/components/org/hierarchyLive';
import type { OrgNode, OrgNodeType } from '@/components/org/hierarchyTree';
import { deactivateMember, reactivateMember, reactivateOrg, purgeDate, ARCHIVE_YEARS } from '@/lib/deactivation';
const ARCHIVE_YEARS_LABEL = `${ARCHIVE_YEARS} years`;
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { visibleAudit, VIEW_AS_IN_LIST } from '@/lib/auditViewAs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Building2, Users, Mail, ScrollText, Settings,
  LayoutDashboard, Plus, Copy, Loader2, Search,
  Pause, Play, Trash2, ChevronLeft, Save, KeyRound,
  Shield, AlertCircle, Clock, Tag, LogOut, TrendingUp,
  UserPlus, UserX, Download, ChevronDown, ChevronRight,
  Clipboard, Bug, Wrench, MessageSquare, Inbox, Network, Eye, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { INVITE_TTL_DAYS, countPending, inviteStatus, partitionInvites } from '@/components/org/inviteStatus';
import { sanitizeError } from '@/lib/errorMessages';
import { logAudit } from '@/lib/audit';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { exportUsersAsCsv, exportAuditAsCsv, exportUserDataReport } from '@/lib/exportUtils';
import { diagnoseError, authDiagnosticsFor } from '@/lib/errorDiagnosis';
import { describeWho, sessionLabel, deliveryLabel, describeUserAgent } from '@/lib/errorIdentity';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import SupportReplyBox from '@/components/SupportReplyBox';
import { awaitingSupport } from '@/lib/supportTickets';
import { filterBySection, sectionCounts, supportCategoryLabel, supportSectionLabel } from '@/lib/supportFields';
import { formatDistanceToNow, format, differenceInDays, subDays, eachWeekOfInterval, startOfWeek } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { subscriptionPillClass, subscriptionStatusLabel } from '@/lib/subscriptionStatus';

// ─── Billing Info Component (reads from org_billing table) ─────────
function OrgBillingInfo({ orgId, copyToClipboard }: { orgId: string; copyToClipboard: (t: string) => void }) {
  const { data: billing } = useQuery({
    queryKey: ['org-billing', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('org_billing')
        .select('stripe_customer_id, stripe_subscription_id')
        .eq('org_id', orgId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  if (!billing?.stripe_customer_id && !billing?.stripe_subscription_id) return null;
  return (
    <div className="space-y-2 pt-2 border-t">
      <Label className="text-muted-foreground text-xs">Stripe</Label>
      {billing.stripe_customer_id && (
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className="text-muted-foreground">Customer:</span>
          <span>{billing.stripe_customer_id}</span>
          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(billing.stripe_customer_id!)}><Copy className="h-3 w-3" /></Button>
        </div>
      )}
      {billing.stripe_subscription_id && (
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className="text-muted-foreground">Subscription:</span>
          <span>{billing.stripe_subscription_id}</span>
          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(billing.stripe_subscription_id!)}><Copy className="h-3 w-3" /></Button>
        </div>
      )}
    </div>
  );
}

// ─── Data Fetchers ────────────────────────────────────────────────

async function fetchOrgs() {
  // The platform root (Tactical Tax) is infrastructure, not a customer org.
  // It must never be listable, suspendable, or touchable from any panel view.
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('is_platform_root', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// Deactivation columns (20260929000002) are read off the row when present;
// the generated types don't know them yet, so they're reached through deact().
type Deactivatable = { deactivated_at?: string | null; deactivated_by?: string | null };
const deact = (x: unknown): Deactivatable => (x ?? {}) as Deactivatable;

async function fetchAllProfiles() {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')   // includes deactivated_at / deactivated_by once 20260929000002 is applied
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

async function fetchAllRoles() {
  const { data, error } = await supabase.from('user_roles').select('user_id, role, org_id');
  if (error) throw error;
  return data ?? [];
}

async function fetchInvites() {
  const { data, error } = await supabase
    .from('org_invites')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

async function fetchAuditLog(showViewAs: boolean) {
  let q = supabase
    .from('audit_log')
    .select('*');
  // View-as rows are kept but hidden by default; leaving them out of the query keeps the
  // 200-row window full of real events instead of start/end pairs.
  if (!showViewAs) q = q.not('action', 'in', VIEW_AS_IN_LIST);
  const { data, error } = await q
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data ?? [];
}

async function fetchPlatformSettings() {
  const { data, error } = await supabase.from('platform_settings').select('*');
  if (error) throw error;
  return data ?? [];
}

async function fetchErrorLog() {
  const { data, error } = await supabase
    .from('error_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data ?? [];
}

async function fetchAnnouncements() {
  const { data, error } = await supabase.from('platform_announcements').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

async function fetchFeatureFlags() {
  const { data, error } = await supabase.from('feature_flags').select('*').order('flag_name');
  if (error) throw error;
  return data ?? [];
}

async function fetchSupportTickets() {
  const { data, error } = await supabase.from('support_tickets' as any).select('*').order('created_at', { ascending: false }).limit(200);
  if (error) throw error;
  return (data ?? []) as any[];
}


// ─── Badges ──────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, string> = {
  active: 'default',
  trialing: 'secondary',
  past_due: 'destructive',
  canceled: 'outline',
  suspended: 'destructive',
  exempt: 'outline',
};

// Colours live in src/lib/subscriptionStatus.ts so Settings → Billing renders
// the same status the same way; this console used to be the only place that
// knew `exempt` was a good thing.
function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block text-[10.5px] font-bold px-2 py-0.5 rounded-full capitalize whitespace-nowrap ${subscriptionPillClass(status)}`}>
      {subscriptionStatusLabel(status)}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const variant = ({ super_admin: 'default', platform_admin: 'default', org_owner: 'default', org_admin: 'secondary', agent: 'outline' } as any)[role] ?? 'outline';
  return <Badge variant={variant} className="text-xs">{role.replace('_', ' ')}</Badge>;
}

// ─── Main Component ──────────────────────────────────────────────

export default function SuperAdmin() {
  const { user, isSuperAdmin, isPlatformAdmin, canAccessAdminPanel, org, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Panel position survives round trips (exiting impersonation remounts this
  // page; refreshes too). Restore the tab you were on instead of dumping back
  // to the queue — the app-wide "back returns to where you were" principle.
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('sa.tab') ?? 'overview');
  useEffect(() => { sessionStorage.setItem('sa.tab', activeTab); }, [activeTab]);
  // Clicking Network in the rail is a declaration of "take me to the top" —
  // it wipes the tree's drill memory and remounts it fresh. Round-trip
  // restoration (impersonation exit, refresh) is untouched: those remount
  // WITHOUT a rail click, so the position survives.
  const [networkKey, setNetworkKey] = useState(0);
  // The table's memory lives under these keys (TeamManagementPage.tsx): the
  // open Manage sheet, opened branches, the search, and an agent's client grid.
  const resetNetwork = () => {
    for (const k of ['tm.selected', 'tm.expanded', 'tm.search', 'tm.agent']) sessionStorage.removeItem(k);
    setNetworkKey(k => k + 1);
    switchTab('network');
  };

  // Stage-1 rail: replaces the 11-tab strip with three grouped destinations.
  // Same tab values and content bodies underneath — navigation chrome only.
  // Reports = Revenue|Audit; Configuration = Flags|Announcements|Settings
  // (announcements only for platform admins). Same side effects the old
  // TabsList onValueChange performed.
  const switchTab = (v: string) => {
    // Configuration is one page under 'flags' for super admins — legacy
    // sub-tab values (persisted sa.tab, old links) land there.
    const dest = isSuperAdmin && (v === 'announcements' || v === 'settings') ? 'flags' : v;
    setActiveTab(dest);
    setSelectedOrgId(null);
    setSelectedUserId(null);
    if (dest === 'errors') setNewErrorCount(0);
  };
  const REPORTS_TABS = ['revenue', 'audit'];
  const CONFIG_TABS = isSuperAdmin ? ['flags', 'announcements', 'settings'] : ['announcements'];

  // A super admin whose persisted tab predates the one-page Configuration
  // (sa.tab = 'announcements'/'settings') lands on the page, not a blank.
  React.useEffect(() => {
    if (isSuperAdmin && (activeTab === 'announcements' || activeTab === 'settings')) setActiveTab('flags');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);
  const [searchQuery, setSearchQuery] = useState('');

  // Server-side verification: super_admin OR platform_admin can open this page.
  const { data: serverVerified, isLoading: verifying } = useQuery({
    queryKey: ['sa-verify'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .in('role', ['super_admin', 'platform_admin']);
      if (error) return false;
      return (data ?? []).length > 0;
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  // Drill-down state
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => sessionStorage.getItem('sa.org'));
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => sessionStorage.getItem('sa.user'));
  useEffect(() => { if (selectedOrgId) sessionStorage.setItem('sa.org', selectedOrgId); else sessionStorage.removeItem('sa.org'); }, [selectedOrgId]);
  useEffect(() => { if (selectedUserId) sessionStorage.setItem('sa.user', selectedUserId); else sessionStorage.removeItem('sa.user'); }, [selectedUserId]);

  // Org detail fields
  const [orgDetailMaxSeats, setOrgDetailMaxSeats] = useState('');
  const [orgDetailStatus, setOrgDetailStatus] = useState('');
  const [orgDetailPlan, setOrgDetailPlan] = useState('');
  const [orgDetailTrialEndsAt, setOrgDetailTrialEndsAt] = useState('');

  // Filters
  const [orgStatusFilter, setOrgStatusFilter] = useState('all');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [auditActionFilter, setAuditActionFilter] = useState('all');
  const [auditDateFilter, setAuditDateFilter] = useState('all');
  const [auditSearch, setAuditSearch] = useState('');

  // Settings
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({});
  const [savingSettings, setSavingSettings] = useState(false);

  // Change role dialog
  const [changeRoleOpen, setChangeRoleOpen] = useState(false);
  const [changeRoleUserId, setChangeRoleUserId] = useState<string | null>(null);
  const [changeRoleOrgId, setChangeRoleOrgId] = useState<string | null>(null);
  const [changeRoleOldRole, setChangeRoleOldRole] = useState('');
  const [changeRoleNewRole, setChangeRoleNewRole] = useState('');

  // Invite lists: both default to what needs attention (unaccepted), one
  // click flips to accepted. A page of every invite ever is never the default.
  const [orgInviteView, setOrgInviteView] = useState<'open' | 'accepted'>('open');
  const [invitesFilter, setInvitesFilter] = useState<'open' | 'accepted'>('open');
  // Org-hub invite dialog — the super-admin way to add a member to ANY org,
  // including platform-staff-owned ones the Network tree deliberately hides.
  const [orgInviteOpen, setOrgInviteOpen] = useState(false);
  const [orgInviteEmail, setOrgInviteEmail] = useState('');
  const [orgInviteRole, setOrgInviteRole] = useState<'agent' | 'org_admin'>('agent');
  const [orgInviteLink, setOrgInviteLink] = useState('');
  // '' = the dialog shows an org picker (opened from a tab header); an id =
  // prefilled (opened from that org's hub). Every customer org is pickable,
  // including platform-staff-owned ones the Network tree hides.
  const [orgInviteOrgId, setOrgInviteOrgId] = useState('');
  const openInviteDialog = (orgId: string) => {
    setOrgInviteOrgId(orgId); setOrgInviteEmail(''); setOrgInviteRole('agent'); setOrgInviteLink(''); setOrgInviteOpen(true);
  };

  // Batch 1 — Create user dialog
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserEmail, setCreateUserEmail] = useState('');
  const [createUserName, setCreateUserName] = useState('');
  const [createUserRole, setCreateUserRole] = useState('agent');
  const [createUserOrgId, setCreateUserOrgId] = useState('none');

  // Batch 1 — Delete user dialog
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState<string | null>(null);
  const [deleteUserConfirmEmail, setDeleteUserConfirmEmail] = useState('');

  // Deactivate / Delete an org (BranchActionDialog: "all of it, or let me
  // choose?"), and Deactivate a person. Drew, 2026-09-29: the Admin Panel
  // has all three verbs — Deactivate, Delete (platform staff, test junk),
  // Suspend (billing). Downline has Deactivate and Suspend only.
  const [orgAction, setOrgAction] = useState<{ mode: BranchActionMode; org: OrgNode } | null>(null);
  const [deactivateUserTarget, setDeactivateUserTarget] = useState<string | null>(null);
  const [deactivateUserConfirm, setDeactivateUserConfirm] = useState('');

  // Batch 1 — Bulk selection
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Batch 2 — Announcement form
  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [announcementType, setAnnouncementType] = useState<'info'|'warning'|'critical'>('info');

  // Batch 2 — Impersonation
  const { startImpersonation, isImpersonating, impersonatedProfile, stopImpersonation } = useImpersonation();

  // Batch 3 — Error log filters
  const [errorTypeFilter, setErrorTypeFilter] = useState('all');
  const [errorShowAcked, setErrorShowAcked] = useState(false);
  const [expandedErrorId, setExpandedErrorId] = useState<string | null>(null);
  const [newFlagName, setNewFlagName] = useState('');

  // Queries
  const { data: allOrgs = [] } = useQuery({ queryKey: ['sa-orgs'], queryFn: fetchOrgs, enabled: canAccessAdminPanel });
  const { data: allProfiles = [] } = useQuery({ queryKey: ['sa-profiles'], queryFn: fetchAllProfiles, enabled: canAccessAdminPanel });
  const { data: roles = [] } = useQuery({ queryKey: ['sa-roles'], queryFn: fetchAllRoles, enabled: canAccessAdminPanel });
  // Deactivated orgs (20260929000002) are off every list; they live only in
  // the Organizations view's "Deactivated" chip, with Reactivate.
  const orgs = React.useMemo(() => allOrgs.filter(o => !deact(o).deactivated_at), [allOrgs]);
  const deactivatedOrgs = React.useMemo(() => allOrgs.filter(o => !!deact(o).deactivated_at), [allOrgs]);
  // Super admin accounts are platform infrastructure, not users — they never
  // appear in any panel list (Users tab, org members, activity, exports), for
  // anyone. Filtering at the source covers every consumer of `profiles`.
  // Deactivated people likewise leave `profiles`; the Users view's
  // "Deactivated" switch lists them from `deactivatedProfiles`.
  const knownProfiles = React.useMemo(() => {
    const superAdminIds = new Set(roles.filter(r => r.role === 'super_admin').map(r => r.user_id));
    return allProfiles.filter(p => !superAdminIds.has(p.id));
  }, [allProfiles, roles]);
  const profiles = React.useMemo(() => knownProfiles.filter(p => p.status !== 'deactivated'), [knownProfiles]);
  const deactivatedProfiles = React.useMemo(() => knownProfiles.filter(p => p.status === 'deactivated'), [knownProfiles]);
  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState(false);
  // The Network tree, for the org Deactivate / Delete dialog's "let me choose"
  // destinations. An org outside it (owned by platform staff, or tree not
  // loaded yet) gets the "all of it" path only.
  const { data: hierarchy } = useHierarchyTree();
  const orgNodeFor = React.useCallback((orgId: string): OrgNode | null => {
    const walk = (n: OrgNode): OrgNode | null => {
      if (n.id === orgId) return n;
      for (const c of n.children) { const r = walk(c); if (r) return r; }
      return null;
    };
    return hierarchy?.root ? walk(hierarchy.root) : null;
  }, [hierarchy]);
  // Orgs owned by platform staff (a super admin or platform admin, e.g.
  // Tactical Tax and Estate Planning) are listed for the super admin alone,
  // and only the super admin may change, suspend, deactivate or delete one
  // (20260928000002, 20260929000002). `orgs` keeps them for the invite and
  // flag pickers.
  const customerOrgs = React.useMemo(() => {
    if (isSuperAdmin) return orgs;
    const staffIds = new Set(roles.filter(r => r.role === 'super_admin' || r.role === 'platform_admin').map(r => r.user_id));
    return orgs.filter(o => !staffIds.has(o.owner_id));
  }, [orgs, roles, isSuperAdmin]);
  const customerDeactivatedOrgs = React.useMemo(() => {
    if (isSuperAdmin) return deactivatedOrgs;
    const staffIds = new Set(roles.filter(r => r.role === 'super_admin' || r.role === 'platform_admin').map(r => r.user_id));
    return deactivatedOrgs.filter(o => !staffIds.has(o.owner_id));
  }, [deactivatedOrgs, roles, isSuperAdmin]);
  const { data: invites = [] } = useQuery({ queryKey: ['sa-invites'], queryFn: fetchInvites, enabled: canAccessAdminPanel });
  const [showViewAs, setShowViewAs] = useState(false);
  const { data: auditLog = [] } = useQuery({ queryKey: ['sa-audit', showViewAs], queryFn: () => fetchAuditLog(showViewAs), enabled: canAccessAdminPanel });
  const { data: settings = [] } = useQuery({ queryKey: ['sa-settings'], queryFn: fetchPlatformSettings, enabled: canAccessAdminPanel });

  const { data: announcements = [] } = useQuery({ queryKey: ['sa-announcements'], queryFn: fetchAnnouncements, enabled: canAccessAdminPanel });
  const { data: featureFlags = [] } = useQuery({ queryKey: ['sa-feature-flags'], queryFn: fetchFeatureFlags, enabled: canAccessAdminPanel });
  // errorLog stays super_admin-only — platform_admin must not see error tracking.
  const { data: errorLog = [], refetch: refetchErrors } = useQuery({ queryKey: ['sa-error-log'], queryFn: fetchErrorLog, enabled: isSuperAdmin });
  const { data: supportTickets = [] } = useQuery({ queryKey: ['sa-support-tickets'], queryFn: fetchSupportTickets, enabled: canAccessAdminPanel });

  // Support ticket state
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(() => sessionStorage.getItem('sa.ticket'));
  useEffect(() => { if (selectedTicketId) sessionStorage.setItem('sa.ticket', selectedTicketId); else sessionStorage.removeItem('sa.ticket'); }, [selectedTicketId]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('open');
  const [ticketSectionFilter, setTicketSectionFilter] = useState('all');

  // Batch 5 — Realtime subscription: push new errors into query cache
  const [newErrorCount, setNewErrorCount] = React.useState(0);
  React.useEffect(() => {
    if (!isSuperAdmin) return;
    const channel = supabase
      .channel('sa-error-log-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'error_log' }, payload => {
        queryClient.setQueryData(['sa-error-log'], (old: any[] = []) => [payload.new, ...old]);
        setNewErrorCount(n => n + 1);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isSuperAdmin]);

  // Derived maps
  const rolesMap = React.useMemo(() => {
    const m = new Map<string, { role: string; org_id: string | null }[]>();
    for (const r of roles) {
      const existing = m.get(r.user_id) ?? [];
      existing.push({ role: r.role, org_id: r.org_id });
      m.set(r.user_id, existing);
    }
    return m;
  }, [roles]);

  const getRoles = (userId: string, orgId?: string | null): string[] =>
    (rolesMap.get(userId) ?? [])
      // Global roles (super_admin, platform_admin) live with org_id = NULL and always show.
      .filter(r => r.role === 'super_admin' || r.role === 'platform_admin' || !orgId || r.org_id === orgId)
      .map(r => r.role);

  // Maps include deactivated rows so a name still resolves and a deactivated
  // org or person can be opened (to be reactivated) from the Deactivated lists.
  const orgMap = React.useMemo(() => {
    const m = new Map<string, typeof allOrgs[0]>();
    for (const o of allOrgs) m.set(o.id, o);
    return m;
  }, [allOrgs]);

  const profileMap = React.useMemo(() => {
    const m = new Map<string, typeof knownProfiles[0]>();
    for (const p of knownProfiles) m.set(p.id, p);
    return m;
  }, [knownProfiles]);

  // Subscription analytics
  const orgsByStatus = React.useMemo(() => {
    const c: Record<string, number> = {};
    for (const o of orgs) c[o.subscription_status] = (c[o.subscription_status] ?? 0) + 1;
    return c;
  }, [orgs]);

  const expiredTrialOrgs = React.useMemo(() =>
    orgs.filter(o => o.subscription_status === 'trialing' && o.trial_ends_at && new Date(o.trial_ends_at) < new Date()),
    [orgs]);

  const expiringSoonOrgs = React.useMemo(() => {
    const now = new Date();
    const in7 = new Date(now.getTime() + 7 * 86400000);
    return orgs.filter(o =>
      o.subscription_status === 'trialing' &&
      o.trial_ends_at &&
      new Date(o.trial_ends_at) > now &&
      new Date(o.trial_ends_at) < in7
    );
  }, [orgs]);

  const needsAttentionOrgs = React.useMemo(() => [
    ...expiredTrialOrgs,
    ...orgs.filter(o => o.subscription_status === 'past_due' || o.subscription_status === 'suspended'),
  ], [orgs, expiredTrialOrgs]);

  const pendingInvites = countPending(invites);
  const invitesPartition = React.useMemo(() => partitionInvites(invites), [invites]);

  // ─── Mutations ────────────────────────────────────────────────


  const suspendOrgMut = useMutation({
    mutationFn: async ({ orgId, suspend }: { orgId: string; suspend: boolean }) => {
      const { error } = await supabase
        .from('organizations')
        .update({ subscription_status: suspend ? 'suspended' : 'active' })
        .eq('id', orgId);
      if (error) throw error;
      return { orgId, suspend };
    },
    onSuccess: ({ orgId, suspend }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-orgs'] });
      logAudit(suspend ? 'org.suspended' : 'org.reactivated', orgId, 'organization', orgId);
      toast.success(suspend ? "Organization suspended" : "Organization reactivated");
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const updateOrgMut = useMutation({
    mutationFn: async ({ orgId, updates, auditAction }: { orgId: string; updates: Record<string, any>; auditAction?: string }) => {
      const { error } = await supabase.from('organizations').update(updates).eq('id', orgId);
      if (error) throw error;
      return { orgId, updates, auditAction };
    },
    onSuccess: ({ orgId, updates, auditAction }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-orgs'] });
      logAudit(auditAction ?? 'org.updated', orgId, 'organization', orgId, updates);
      toast.success('Organization updated');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const deleteInviteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('org_invites').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['sa-invites'] });
      logAudit('invite.revoked', null, 'invite', id);
      toast.success('Invite revoked');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Resend restarts the invite's clock on the SAME token and emails it again;
  // works on expired invites too (that is the point). RLS decides who may:
  // platform staff can resend any invite.
  const resendInviteMut = useMutation({
    mutationFn: async (inv: { id: string; token: string; email: string | null; org_id: string | null }) => {
      const { data, error } = await supabase.functions.invoke('send-invite-email', {
        body: { token: inv.token, resend: true },
      });
      if (error) {
        let msg = error.message;
        try {
          const body = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
          if (body?.error) msg = body.error;
        } catch { /* body was not JSON — keep the transport message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return { inv, emailSent: !!data?.emailSent };
    },
    onSuccess: ({ inv, emailSent }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-invites'] });
      logAudit('invite.sent', inv.org_id, 'invite', inv.id, { resend: true, email: inv.email ?? 'open' });
      if (emailSent) {
        toast.success('Invite resent', { description: `Email sent to ${inv.email}. Good for another ${INVITE_TTL_DAYS} days.` });
      } else {
        toast.success('Invite renewed', { description: `Good for another ${INVITE_TTL_DAYS} days. Copy the link and send it yourself.` });
      }
    },
    onError: (err: unknown) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Org-hub member invite. Same edge function the org's own Team page uses;
  // the "Super admins manage all invites" RLS policy is what lets platform
  // staff insert an org_member invite for an org they are not a member of.
  const orgInviteMut = useMutation({
    mutationFn: async ({ orgId, orgName }: { orgId: string; orgName: string }) => {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: orgInviteEmail.trim() || undefined,
          type: 'org_member',
          org_id: orgId,
          role: orgInviteRole,
          org_name: orgName,
        },
      });
      if (error) {
        let msg = error.message;
        try {
          const body = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
          if (body?.error) msg = body.error;
        } catch { /* body was not JSON — keep the transport message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return { data, orgId };
    },
    onSuccess: ({ data, orgId }) => {
      setOrgInviteLink(`${window.location.origin}/invite/${data.token}`);
      queryClient.invalidateQueries({ queryKey: ['sa-invites'] });
      logAudit('invite.created', orgId, 'invite', data.id, { email: orgInviteEmail.trim() || 'open', role: orgInviteRole });
      if (data.emailSent) {
        toast.success('Invite sent', { description: `Email sent to ${orgInviteEmail.trim()}` });
      } else if (orgInviteEmail.trim()) {
        toast.success('Invite created', { description: 'Could not send email. Share the link below.' });
      } else {
        toast.success('Invite created');
      }
    },
    onError: (err: unknown) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const updateUserStatusMut = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: string }) => {
      const { error } = await supabase.from('profiles').update({ status }).eq('id', userId);
      if (error) throw error;
      return { userId, status };
    },
    onSuccess: ({ userId, status }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-profiles'] });
      const p = profileMap.get(userId);
      logAudit(status === 'active' ? 'user.reactivated' : 'user.suspended', p?.org_id ?? null, 'user', userId);
      toast.success('User updated');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const changeUserRoleMut = useMutation({
    mutationFn: async ({ userId, orgId, oldRole, newRole }: { userId: string; orgId: string; oldRole: string; newRole: string }) => {
      const { data, error } = await supabase.functions.invoke('admin-user-ops', {
        body: { action: 'change_role', user_id: userId, org_id: orgId, new_role: newRole },
      });
      if (error) {
        let msg = error.message;
        try { const body = await (error as any).context?.json(); if (body?.error) msg = body.error; } catch { /* not JSON: keep the generic message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return { userId, orgId, oldRole, newRole };
    },
    onSuccess: ({ userId, orgId, oldRole, newRole }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-roles'] });
      logAudit('role.changed', orgId, 'user', userId, { from: oldRole, to: newRole });
      toast.success('Role updated');
      setChangeRoleOpen(false);
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // ── Batch 1 mutations ──────────────────────────────────────────────────────

  // Reset Password removed — users can self-service via login screen

  const createUserMut = useMutation({
    mutationFn: async ({ email, name, role, orgId }: { email: string; name: string; role: string; orgId: string }) => {
      const { data, error } = await supabase.functions.invoke('admin-user-ops', {
        body: { action: 'create_user', email, name, role, org_id: orgId === 'none' ? null : orgId },
      });
      if (error) {
        let msg = error.message;
        try { const body = await (error as any).context?.json(); if (body?.error) msg = body.error; } catch { /* not JSON: keep the generic message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return data as { user_id: string; org_id: string; emailSent?: boolean };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sa-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['sa-roles'] });
      queryClient.invalidateQueries({ queryKey: ['sa-orgs'] });
      logAudit('user.created', data.org_id, 'user', data.user_id, { email: createUserEmail, role: createUserRole, email_sent: !!data.emailSent });
      // The account has no password until they follow the setup email, so
      // say plainly whether that email went out.
      if (data.emailSent) {
        toast.success('User created', { description: `Password-setup email sent to ${createUserEmail}.` });
      } else {
        toast.error('User created, but the setup email did not send', {
          description: `${createUserEmail} has no password yet. Have them use "Forgot password" on the sign-in page to set one.`,
          duration: 10000,
        });
      }
      setCreateUserOpen(false);
      setCreateUserEmail(''); setCreateUserName(''); setCreateUserRole('agent'); setCreateUserOrgId('none');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const deleteUserMut = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-user-ops', {
        body: { action: 'delete_user', user_id: userId },
      });
      if (error) {
        let msg = error.message;
        try { const body = await (error as any).context?.json(); if (body?.error) msg = body.error; } catch { /* not JSON: keep the generic message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      return userId;
    },
    onSuccess: (userId) => {
      queryClient.invalidateQueries({ queryKey: ['sa-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['sa-roles'] });
      queryClient.invalidateQueries({ queryKey: ['sa-orgs'] });
      toast.success('User permanently deleted');
      setDeleteUserOpen(false);
      setDeleteUserTarget(null);
      setDeleteUserConfirmEmail('');
      if (selectedUserId === userId) setSelectedUserId(null);
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Grant/Revoke Platform Admin — super-admin-only (the RLS WITH CHECK on
  // user_roles also enforces this at the DB layer; the UI just hides the button
  // for platform admins). Platform admin is global (org_id = NULL) so it
  // coexists with the user's existing org-scoped role.
  const platformAdminMut = useMutation({
    mutationFn: async ({ userId, grant }: { userId: string; grant: boolean }) => {
      if (grant) {
        const { error } = await supabase.from('user_roles').insert({
          user_id: userId, role: 'platform_admin', org_id: null,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('user_roles')
          .delete().eq('user_id', userId).eq('role', 'platform_admin').is('org_id', null);
        if (error) throw error;
      }
      return { userId, grant };
    },
    onSuccess: ({ userId, grant }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-roles'] });
      const profile = profileMap.get(userId);
      const who = profile?.full_name || profile?.email || 'user';
      toast.success(grant ? `${who} is now a Platform Admin` : `Platform Admin revoked from ${who}`);
      logAudit('role.changed', null, 'user', userId, { platform_admin: grant });
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const afterDeactivation = () => {
    queryClient.invalidateQueries({ queryKey: ['sa-orgs'] });
    queryClient.invalidateQueries({ queryKey: ['sa-profiles'] });
    queryClient.invalidateQueries({ queryKey: ['sa-roles'] });
    queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
  };

  // deactivate_member / reactivate_member / reactivate_org carry their own
  // gates and write their own audit rows (20260929000002).
  const deactivateUserMut = useMutation({
    mutationFn: async (userId: string) => { await deactivateMember(userId); return userId; },
    onSuccess: (userId) => {
      afterDeactivation();
      const who = profileMap.get(userId);
      toast.success(`${who?.full_name || who?.email || 'User'} deactivated`, {
        description: 'Signed out and off every screen. Their clients stay with them. Reactivate from the Deactivated list.',
      });
      setDeactivateUserTarget(null);
      setDeactivateUserConfirm('');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const reactivateUserMut = useMutation({
    mutationFn: async (userId: string) => { await reactivateMember(userId); return userId; },
    onSuccess: (userId) => {
      afterDeactivation();
      const who = profileMap.get(userId);
      toast.success(`${who?.full_name || who?.email || 'User'} reactivated`);
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const reactivateOrgMut = useMutation({
    mutationFn: async (orgId: string) => ({ orgId, people: await reactivateOrg(orgId) }),
    onSuccess: ({ orgId, people }) => {
      afterDeactivation();
      toast.success(`${orgMap.get(orgId)?.name ?? 'Organization'} reactivated`, {
        description: `${people} ${people === 1 ? 'person is' : 'people are'} back.`,
      });
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // The dialog needs a tree node; an org the Network tree doesn't carry gets a
  // bare one, which offers "all of it" only.
  const openOrgAction = (mode: BranchActionMode, o: typeof allOrgs[0]) => {
    const node = orgNodeFor(o.id) ?? {
      id: o.id, type: ((o.org_type as OrgNodeType | null) ?? 'imo'), name: o.name,
      admin: profileMap.get(o.owner_id)?.full_name || profileMap.get(o.owner_id)?.email || 'the owner',
      flags: { alts: true, fia: true }, children: [], clientCount: 0, clients: [],
    };
    setOrgAction({ mode, org: node });
  };

  const bulkOrgMut = useMutation({
    mutationFn: async ({ orgIds, status }: { orgIds: string[]; status: string }) => {
      for (const orgId of orgIds) {
        // A refused write (billing lock, protected org) must not toast success.
        const { error } = await supabase.from('organizations').update({ subscription_status: status }).eq('id', orgId);
        if (error) throw error;
      }
      return { orgIds, status };
    },
    onSuccess: ({ orgIds, status }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-orgs'] });
      for (const orgId of orgIds) {
        logAudit('bulk.org_status_changed', orgId, 'organization', orgId, { status, bulk: true });
      }
      setSelectedOrgIds(new Set());
      toast.success(`${orgIds.length} org(s) updated to ${status}`);
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const bulkUserMut = useMutation({
    mutationFn: async ({ userIds, status }: { userIds: string[]; status: string }) => {
      for (const userId of userIds) {
        await supabase.from('profiles').update({ status }).eq('id', userId);
      }
      return { userIds, status };
    },
    onSuccess: ({ userIds, status }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-profiles'] });
      for (const userId of userIds) {
        logAudit('bulk.user_status_changed', profileMap.get(userId)?.org_id ?? null, 'user', userId, { status, bulk: true });
      }
      setSelectedUserIds(new Set());
      toast.success(`${userIds.length} user(s) updated to ${status}`);
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // ── Batch 2 mutations ──────────────────────────────────────────────────────

  const createAnnouncementMut = useMutation({
    mutationFn: async ({ message, type }: { message: string; type: string }) => {
      // Deactivate all previous before creating new
      await supabase.from('platform_announcements').update({ active: false }).eq('active', true);
      const { data, error } = await supabase.from('platform_announcements')
        .insert({ message, type, active: true, created_by: user!.id }).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sa-announcements'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      logAudit('announcement.created', null, 'announcement', data.id, { type: data.type });
      setAnnouncementMsg('');
      toast.success('Announcement published');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const toggleAnnouncementMut = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from('platform_announcements').update({ active }).eq('id', id);
      if (error) throw error;
      return { id, active };
    },
    onSuccess: ({ id, active }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-announcements'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      logAudit('announcement.updated', null, 'announcement', id, { active });
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const deleteAnnouncementMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('platform_announcements').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['sa-announcements'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      logAudit('announcement.deleted', null, 'announcement', id);
      toast.success('Announcement deleted');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const toggleFlagMut = useMutation({
    mutationFn: async ({ id, enabled_globally }: { id: string; enabled_globally: boolean }) => {
      const { error } = await supabase.from('feature_flags').update({ enabled_globally, updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
      return { id, enabled_globally };
    },
    onSuccess: ({ id, enabled_globally }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      logAudit('feature_flag.toggled', null, 'feature_flag', id, { enabled_globally });
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const toggleFlagForOrgMut = useMutation({
    mutationFn: async ({ id, orgId, add }: { id: string; orgId: string; add: boolean }) => {
      const flag = featureFlags.find(f => f.id === id);
      if (!flag) throw new Error('Flag not found');
      const orgs: string[] = flag.enabled_for_orgs ?? [];
      const updated = add ? [...new Set([...orgs, orgId])] : orgs.filter((o: string) => o !== orgId);
      const { error } = await supabase.from('feature_flags').update({ enabled_for_orgs: updated, updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
      return { id, orgId, add };
    },
    onSuccess: ({ id, orgId, add }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      logAudit('feature_flag.toggled', orgId, 'feature_flag', id, { org_enabled: add });
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // Per-USER pin — test a feature on one advisor without lighting it up for
  // the rest of their org. Mirrors the org pin; `enabled_for_users` isn't in
  // the auto-generated Supabase types yet (Drew regenerates those).
  const toggleFlagForUserMut = useMutation({
    mutationFn: async ({ id, userId, add }: { id: string; userId: string; add: boolean }) => {
      const flag = featureFlags.find(f => f.id === id) as (typeof featureFlags)[0] & { enabled_for_users?: string[] } | undefined;
      if (!flag) throw new Error('Flag not found');
      const users: string[] = flag.enabled_for_users ?? [];
      const updated = add ? [...new Set([...users, userId])] : users.filter((u: string) => u !== userId);
      const { error } = await supabase.from('feature_flags')
        .update({ enabled_for_users: updated, updated_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('id', id);
      if (error) throw error;
      return { id, userId, add };
    },
    onSuccess: ({ id, userId, add }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      logAudit('feature_flag.toggled', profileMap.get(userId)?.org_id ?? null, 'feature_flag', id, { user_enabled: add, user_id: userId });
    },
    onError: (err: unknown) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const createFlagMut = useMutation({
    mutationFn: async (flagName: string) => {
      const { data, error } = await supabase.from('feature_flags')
        .insert({ flag_name: flagName, enabled_globally: false, enabled_for_orgs: [], created_by: user!.id })
        .select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sa-feature-flags'] });
      toast.success('Flag created');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // ── Batch 3 mutations ──────────────────────────────────────────────────────

  const acknowledgeErrorMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('error_log').update({ acknowledged: true }).eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sa-error-log'] });
      logAudit('error.reviewed', null, 'error_log', undefined);
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const acknowledgeAllErrorsMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('error_log').update({ acknowledged: true }).eq('acknowledged', false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sa-error-log'] });
      toast.success('All errors acknowledged');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  // ── Support ticket mutations ──────────────────────────────────────────────
  const updateTicketMut = useMutation({
    mutationFn: async ({ ticketId, updates }: { ticketId: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('support_tickets' as any).update(updates).eq('id', ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sa-support-tickets'] });
      toast.success('Ticket updated');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const toggleDiagnosticMut = useMutation({
    mutationFn: async ({ userId, enable }: { userId: string; enable: boolean }) => {
      const { error } = await supabase.rpc('toggle_diagnostic_mode', { p_user_id: userId, p_enable: enable });
      if (error) throw error;
      return { userId, enable };
    },
    onSuccess: ({ userId, enable }) => {
      queryClient.invalidateQueries({ queryKey: ['sa-profiles'] });
      const p = profileMap.get(userId);
      logAudit(enable ? 'diagnostic.enabled' : 'diagnostic.disabled', p?.org_id ?? null, 'user', userId, { duration_hours: 24 });
      toast.success(enable ? "Diagnostic mode enabled (24h)" : "Diagnostic mode disabled");
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // handleResetPassword removed — replaced by forceResetPasswordMut (edge fn w/ service role)

  // Org detail — sync fields when selection changes
  const selectedOrg = selectedOrgId ? orgMap.get(selectedOrgId) : null;
  const selectedUserProfile = selectedUserId ? profileMap.get(selectedUserId) : null;

  React.useEffect(() => {
    if (selectedOrg) {
      setOrgDetailMaxSeats(String(selectedOrg.max_seats));
      setOrgDetailStatus(selectedOrg.subscription_status);
      setOrgDetailPlan(selectedOrg.subscription_plan ?? 'standard');
      setOrgDetailTrialEndsAt(selectedOrg.trial_ends_at ? selectedOrg.trial_ends_at.slice(0, 10) : '');
    }
  }, [selectedOrg]);

  // Settings helpers
  const getSettingValue = (key: string) =>
    editedSettings[key] ?? JSON.stringify(settings.find(s => s.key === key)?.value ?? '');

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    // Each write is checked. A refused UPDATE comes back as zero rows, not an
    // error, and this used to toast "Settings saved" either way.
    const failed: string[] = [];
    for (const [key, rawVal] of Object.entries(editedSettings)) {
      let value: any;
      try { value = JSON.parse(rawVal); } catch { value = rawVal; }
      const { data, error } = await supabase
        .from('platform_settings')
        .update({ value, updated_by: user!.id })
        .eq('key', key)
        .select('key');
      if (error || !data || data.length === 0) {
        failed.push(key);
        continue;
      }
      logAudit('settings.updated', null, 'setting', undefined, { key, value });
    }
    queryClient.invalidateQueries({ queryKey: ['sa-settings'] });
    setSavingSettings(false);
    if (failed.length > 0) {
      setEditedSettings(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => failed.includes(k))));
      toast.error('Some settings were not saved', {
        description: failed.map(k => k.replace(/_/g, ' ')).join(', '),
      });
      return;
    }
    setEditedSettings({});
    toast.success('Settings saved');
  };

  // Audit filtering
  const filteredAudit = React.useMemo(() => {
    let filtered = visibleAudit(auditLog, showViewAs);
    if (auditActionFilter !== 'all') filtered = filtered.filter(l => l.action.includes(auditActionFilter));
    if (auditSearch) {
      const q = auditSearch.toLowerCase();
      filtered = filtered.filter(l => {
        const actor = profileMap.get(l.actor_id);
        return actor?.email?.toLowerCase().includes(q) || actor?.full_name?.toLowerCase().includes(q);
      });
    }
    if (auditDateFilter !== 'all') {
      const now = Date.now();
      const ms = ({ '24h': 86400000, '7d': 604800000, '30d': 2592000000 } as any)[auditDateFilter] ?? 0;
      if (ms) filtered = filtered.filter(l => now - new Date(l.created_at).getTime() < ms);
    }
    return filtered;
  }, [auditLog, showViewAs, auditActionFilter, auditSearch, auditDateFilter, profileMap]);

  const auditActions = React.useMemo(() => [...new Set(auditLog.map(l => l.action))].sort(), [auditLog]);

  // ─── Batch 4 Export handlers ──────────────────────────────────

  const handleExportUsers = () => {
    const rows = profiles.map(p => ({
      full_name: p.full_name ?? '',
      email: p.email ?? '',
      org_name: p.org_id ? (orgMap.get(p.org_id)?.name ?? 'Unknown') : 'None',
      roles: getRoles(p.id, p.org_id).join(', '),
      status: p.status,
      subscription: p.org_id ? (orgMap.get(p.org_id)?.subscription_status ?? '') : '',
      created_at: p.created_at ? format(new Date(p.created_at), 'yyyy-MM-dd HH:mm') : '',
      last_login_at: p.last_sign_in_at ? format(new Date(p.last_sign_in_at), 'yyyy-MM-dd HH:mm') : '',
    }));
    exportUsersAsCsv(rows, `users-export-${format(new Date(), 'yyyyMMdd')}.csv`);
    logAudit('settings.updated', null, 'export', undefined, { type: 'users_csv' });
  };

  const handleExportAudit = () => {
    const rows = filteredAudit.map(l => {
      const actor = profileMap.get(l.actor_id);
      const logOrg = l.org_id ? orgMap.get(l.org_id) : null;
      return {
        created_at: format(new Date(l.created_at), 'yyyy-MM-dd HH:mm:ss'),
        actor_email: actor?.email ?? l.actor_id,
        action: l.action,
        target_type: l.target_type ?? '',
        target_id: l.target_id ?? '',
        org_name: logOrg?.name ?? '',
        details: l.details ? JSON.stringify(l.details) : '',
      };
    });
    exportAuditAsCsv(rows, `audit-log-export-${format(new Date(), 'yyyyMMdd')}.csv`);
    logAudit('settings.updated', null, 'export', undefined, { type: 'audit_csv' });
  };

  const handleExportUserDataReport = async (userId: string) => {
    const p = profileMap.get(userId);
    if (!p) return;
    const [scenariosRes, assignmentsRes, auditRes] = await Promise.all([
      supabase.from('scenarios').select('*').eq('user_id', userId),
      supabase.from('case_assignments').select('*').eq('assigned_to', userId),
      supabase.from('audit_log').select('*').eq('actor_id', userId).order('created_at', { ascending: false }),
    ]);
    const orgData = p.org_id ? orgMap.get(p.org_id) : null;
    const report = {
      generated_at: new Date().toISOString(),
      profile: p,
      organization: orgData ?? null,
      roles: getRoles(userId),
      scenarios: scenariosRes.data ?? [],
      assignments: assignmentsRes.data ?? [],
      audit_entries: auditRes.data ?? [],
    };
    exportUserDataReport(report, p.email ?? userId);
    logAudit('settings.updated', p.org_id ?? null, 'export', userId, { type: 'user_data_report' });
  };

  // ─── Gate ─────────────────────────────────────────────────────

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (serverVerified === false) return <Navigate to="/" replace />;

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-muted/40 px-3 py-3 md:px-5 md:py-4">
      <div className="max-w-[1520px] mx-auto bg-card border rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="px-5 py-2.5 flex items-center gap-3">
          {/* Platform admins also have a regular workspace (My Clients, etc.) and
              need a way back. Super admin doesn't have one — landing is /super-admin.
              Lives top-left, ghost styling like Sign Out (no boxy outline). */}
          {isPlatformAdmin && !isSuperAdmin && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-1.5 text-muted-foreground -ml-2">
              <ChevronLeft className="h-4 w-4" /> Back to App
            </Button>
          )}
          <div className="h-8 w-8 rounded-lg bg-[#0E1729] text-white flex items-center justify-center shrink-0">
            <Shield className="h-4 w-4" />
          </div>
          <div className="flex-1 flex items-baseline gap-2.5 min-w-0">
            <h1 className="text-[15px] font-bold tracking-tight leading-none">Admin Panel</h1>
            <p className="text-xs text-muted-foreground truncate">Platform management · Tactical Tax Genie</p>
          </div>
          <Badge variant="default" className="text-xs">{isSuperAdmin ? 'Super Admin' : 'Platform Admin'}</Badge>
          <Button variant="ghost" size="sm" onClick={signOut} className="gap-1.5 text-muted-foreground">
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="px-5 py-5">
        <Tabs value={activeTab} onValueChange={switchTab}>
          <div className="flex gap-6 items-start">
            {/* ─── Left rail: Work / Accounts / Platform ─── */}
            <aside className="w-52 shrink-0 sticky top-24 space-y-5">
              {([
                {
                  label: 'Work',
                  items: [
                    { v: 'overview', icon: LayoutDashboard, label: 'Queue' },
                    ...(isSuperAdmin ? [
                      { v: 'errors', icon: Bug, label: 'Errors',
                        badge: errorLog.filter(e => !e.acknowledged).length,
                        badgeClass: 'bg-destructive text-destructive-foreground' },
                      { v: 'support', icon: Inbox, label: 'Support',
                        badge: supportTickets.filter((t: any) => t.status === 'open').length,
                        badgeClass: 'bg-primary text-primary-foreground' },
                    ] : []),
                  ],
                },
                {
                  label: 'Accounts',
                  items: [
                    { v: 'network', icon: Network, label: 'Network', go: resetNetwork },
                    { v: 'orgs', icon: Building2, label: 'Organizations' },
                    { v: 'users', icon: Users, label: 'Users' },
                    { v: 'invites', icon: Mail, label: 'Invites',
                      badge: pendingInvites,
                      badgeClass: 'bg-primary text-primary-foreground' },
                  ],
                },
                {
                  label: 'Platform',
                  items: [
                    { v: 'reports', icon: TrendingUp, label: 'Reports', set: REPORTS_TABS, target: 'revenue' },
                    { v: 'config', icon: Settings, label: 'Configuration', set: CONFIG_TABS, target: CONFIG_TABS[0] },
                  ],
                },
              ] as { label: string; items: { v: string; icon: React.ElementType; label: string; badge?: number; badgeClass?: string; set?: string[]; target?: string; go?: () => void }[] }[]).map(group => (
                <div key={group.label}>
                  <p className="px-3 mb-1.5 text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground">{group.label}</p>
                  <div className="space-y-0.5">
                    {group.items.map(item => {
                      const isActive = item.set ? item.set.includes(activeTab) : activeTab === item.v;
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.v}
                          onClick={() => item.go ? item.go() : switchTab(item.target ?? item.v)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                            isActive
                              ? 'bg-primary/10 text-primary font-semibold'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 text-left">{item.label}</span>
                          {(item.badge ?? 0) > 0 && (
                            <span className={`text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center ${item.badgeClass}`}>
                              {item.badge! > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </aside>

            <div className="flex-1 min-w-0">
              {/* Sub-switchers for the merged Reports / Configuration destinations.
                  Content bodies below are the untouched originals — these pills
                  just flip activeTab between sibling values. */}
              {REPORTS_TABS.includes(activeTab) && (
                <div className="flex gap-1 mb-4 bg-muted rounded-lg p-1 w-fit">
                  {[{ v: 'revenue', l: 'Revenue' }, { v: 'audit', l: 'Audit Log' }].map(s => (
                    <button key={s.v} onClick={() => switchTab(s.v)}
                      className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeTab === s.v ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}>
                      {s.l}
                    </button>
                  ))}
                </div>
              )}
              {/* Configuration is one page now — no sub-switcher. */}

          {/* ─── Network: Team Management, inside the panel ─── */}
          <TabsContent value="network">
            <TeamManagementContent key={networkKey} />
          </TabsContent>

          {/* ─── Overview ─── */}
          <TabsContent value="overview">
            {/* Section head */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <h2 className="text-[17px] font-bold tracking-tight">Queue</h2>
                <p className="text-xs text-muted-foreground">What needs you now — everything else can wait.</p>
              </div>
              <Button size="sm" className="h-8 text-xs gap-1" onClick={() => openInviteDialog('')} title="Invite an agent or admin into any org">
                <Plus className="h-3.5 w-3.5" /> Invite
              </Button>
            </div>

            {/* Stat strip — mono numerals, uppercase micro labels */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4">
              {[
                { label: 'Organizations', value: orgs.length },
                { label: 'Users', value: profiles.length },
                { label: 'Active', value: orgsByStatus.active ?? 0 },
                { label: 'Trialing', value: orgsByStatus.trialing ?? 0, tone: (orgsByStatus.trialing ?? 0) > 0 ? 'text-amber-600' : '' },
                { label: 'Attention', value: needsAttentionOrgs.length, tone: needsAttentionOrgs.length > 0 ? 'text-destructive' : '' },
                { label: 'Exempt', value: orgsByStatus.exempt ?? 0 },
              ].map(stat => (
                <Card key={stat.label}>
                  <CardContent className="px-3.5 py-2.5">
                    <p className={`font-mono text-xl font-semibold leading-tight tabular-nums ${stat.tone ?? ''}`}>{stat.value}</p>
                    <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Needs you now — one actionable list: attention orgs + expiring trials */}
              <Card>
                <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-[13.5px] font-bold">Needs you now</CardTitle>
                  <span className="text-[11px] text-muted-foreground">{needsAttentionOrgs.length + expiringSoonOrgs.length} item{needsAttentionOrgs.length + expiringSoonOrgs.length === 1 ? '' : 's'}</span>
                </CardHeader>
                <CardContent className="px-4 py-1">
                  {needsAttentionOrgs.length === 0 && expiringSoonOrgs.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">All accounts are in good standing.</p>
                  ) : (
                    <>
                      {needsAttentionOrgs.slice(0, 6).map(o => {
                        const reason = o.subscription_status === 'suspended' ? 'Suspended'
                          : o.subscription_status === 'past_due' ? 'Past due'
                          : 'Trial expired';
                        const owner = profileMap.get(o.owner_id);
                        return (
                          <div key={o.id} className="flex items-center gap-3 py-2.5 border-b last:border-b-0">
                            <Badge variant="destructive" className="text-[10px] shrink-0">{reason}</Badge>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold truncate">{o.name}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{owner?.email ?? '—'}</p>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => updateOrgMut.mutate({ orgId: o.id, updates: { subscription_status: 'active' }, auditAction: 'subscription.changed' })}>
                                Activate
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500 text-emerald-700"
                                onClick={() => updateOrgMut.mutate({ orgId: o.id, updates: { subscription_status: 'exempt', subscription_plan: 'internal' }, auditAction: 'subscription.changed' })}>
                                Exempt
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {expiringSoonOrgs.map(o => {
                        const daysLeft = differenceInDays(new Date(o.trial_ends_at!), new Date());
                        const owner = profileMap.get(o.owner_id);
                        const newDate = new Date(Date.now() + 30 * 86400000).toISOString();
                        return (
                          <div key={o.id} className="flex items-center gap-3 py-2.5 border-b last:border-b-0">
                            <Badge className="text-[10px] shrink-0 bg-amber-100 text-amber-800 hover:bg-amber-100">Trial ends {daysLeft}d</Badge>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold truncate">{o.name}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{owner?.email ?? '—'}</p>
                            </div>
                            <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                              onClick={() => updateOrgMut.mutate({ orgId: o.id, updates: { trial_ends_at: newDate }, auditAction: 'trial.extended' })}>
                              +30 days
                            </Button>
                          </div>
                        );
                      })}
                      {needsAttentionOrgs.length > 6 && (
                        <p className="text-[11px] text-muted-foreground py-2">+{needsAttentionOrgs.length - 6} more — <button className="underline" onClick={() => { setOrgStatusFilter('attention'); setActiveTab('orgs'); }}>view all</button></p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-4">
                {/* System health — jumps into Errors/Support, super-admin only */}
                {isSuperAdmin && (
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="cursor-pointer hover:border-destructive/50 transition-colors" onClick={() => setActiveTab('errors')}>
                      <CardContent className="px-4 py-3 flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${errorLog.filter(e => !e.acknowledged).length > 0 ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                          <Bug className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-mono text-xl font-semibold leading-tight tabular-nums">{errorLog.filter(e => !e.acknowledged).length}</p>
                          <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Unreviewed errors</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('support')}>
                      <CardContent className="px-4 py-3 flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${supportTickets.filter((t: any) => t.status === 'open').length > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          <Inbox className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-mono text-xl font-semibold leading-tight tabular-nums">{supportTickets.filter((t: any) => t.status === 'open').length}</p>
                          <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Open tickets</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Recent platform activity — mono action chips */}
                <Card>
                  <CardHeader className="px-4 py-3 border-b">
                    <CardTitle className="text-[13.5px] font-bold">Recent platform activity</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 py-1">
                    {auditLog.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">Nothing yet.</p>
                    ) : (
                      auditLog.slice(0, 6).map(log => {
                        const actor = profileMap.get(log.actor_id);
                        return (
                          <div key={log.id} className="flex items-center gap-2.5 py-2 border-b last:border-b-0 text-[12.5px]">
                            <Badge variant="outline" className="text-[10.5px] font-mono shrink-0">{log.action}</Badge>
                            <span className="truncate flex-1 text-foreground/80">{actor?.full_name || actor?.email || 'system'}</span>
                            <span className="text-[11px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</span>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ─── Organizations ─── */}
          <TabsContent value="orgs">
            {selectedOrg ? (() => {
              // Subtree rollup for the identity line — child orgs by type plus
              // every profile living anywhere under this org.
              const kids: typeof orgs = [];
              const walk = (id: string) => { for (const c of orgs) if (c.parent_org_id === id) { kids.push(c); walk(c.id); } };
              walk(selectedOrg.id);
              const subtreeIds = new Set([selectedOrg.id, ...kids.map(k => k.id)]);
              const agencyCount = kids.filter(k => k.org_type === 'agency').length;
              const teamCount = kids.filter(k => k.org_type === 'team').length;
              const memberCount = profiles.filter(p => p.org_id && subtreeIds.has(p.org_id)).length;
              const owner = profileMap.get(selectedOrg.owner_id);
              const rollupParts = [
                agencyCount > 0 ? `${agencyCount} ${agencyCount === 1 ? 'agency' : 'agencies'}` : null,
                teamCount > 0 ? `${teamCount} team${teamCount === 1 ? '' : 's'}` : null,
                `${memberCount} user${memberCount === 1 ? '' : 's'}`,
                owner ? `owner ${owner.full_name || owner.email}` : null,
                owner?.full_name && owner?.email ? owner.email : null,
                `created ${formatDistanceToNow(new Date(selectedOrg.created_at), { addSuffix: true })}`,
              ].filter(Boolean).join(' · ');
              const orgPeople = profiles.filter(p => p.org_id === selectedOrg.id
                && !(rolesMap.get(p.id) ?? []).some(r => r.role === 'super_admin'));
              const orgActivity = auditLog.filter(l => l.org_id === selectedOrg.id).slice(0, 8);
              const orgInvites = partitionInvites(invites.filter((i: any) => i.org_id === selectedOrg.id));
              const orgInviteRows = orgInviteView === 'open' ? orgInvites.open : orgInvites.accepted;
              return (
              <div>
                <button
                  onClick={() => setSelectedOrgId(null)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground mb-3"
                >
                  <ChevronLeft className="h-4 w-4" /> All organizations
                </button>

                {/* Identity band */}
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-[#2D4B6E] text-white flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-[19px] font-bold tracking-tight leading-tight">{selectedOrg.name}</h2>
                        {selectedOrg.org_type && (
                          <span className="text-[9.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded bg-[#2D4B6E] text-white">{selectedOrg.org_type}</span>
                        )}
                        <StatusBadge status={selectedOrg.subscription_status} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{rollupParts}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  {/* Left: billing + pending invites */}
                  <div className="space-y-4">
                  <Card>
                    <CardHeader className="px-4 py-3 border-b">
                      <CardTitle className="text-[13.5px] font-bold">Billing &amp; subscription</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 py-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Subscription status</Label>
                        <Select value={orgDetailStatus} onValueChange={setOrgDetailStatus}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['trialing', 'active', 'past_due', 'canceled', 'suspended', 'exempt'].map(s => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Plan</Label>
                        <Select value={orgDetailPlan} onValueChange={setOrgDetailPlan}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['personal', 'standard', 'team', 'enterprise', 'partner', 'internal'].map(p => (
                              <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Max seats</Label>
                        <Input type="number" className="h-9 font-mono" value={orgDetailMaxSeats} onChange={e => setOrgDetailMaxSeats(e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Trial ends {orgDetailStatus !== 'trialing' && <span className="normal-case tracking-normal">(inactive)</span>}</Label>
                        <Input type="date" className="h-9" value={orgDetailTrialEndsAt} onChange={e => setOrgDetailTrialEndsAt(e.target.value)} disabled={orgDetailStatus !== 'trialing'} />
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" onClick={() => {
                        const prevStatus = selectedOrg.subscription_status;
                        const prevTrialEndsAt = selectedOrg.trial_ends_at;
                        const newTrialEndsAt = orgDetailTrialEndsAt ? new Date(orgDetailTrialEndsAt).toISOString() : selectedOrg.trial_ends_at;
                        const trialExtended = orgDetailStatus === 'trialing' && newTrialEndsAt !== prevTrialEndsAt;
                        const subChanged = orgDetailStatus !== prevStatus || orgDetailPlan !== (selectedOrg.subscription_plan ?? 'standard');
                        updateOrgMut.mutate({
                          orgId: selectedOrg.id,
                          updates: {
                            subscription_status: orgDetailStatus,
                            subscription_plan: orgDetailPlan,
                            max_seats: parseInt(orgDetailMaxSeats) || selectedOrg.max_seats,
                            ...(orgDetailStatus === 'trialing' ? { trial_ends_at: newTrialEndsAt } : {}),
                          },
                          auditAction: trialExtended ? 'trial.extended' : subChanged ? 'subscription.changed' : 'org.updated',
                        });
                      }}>
                        <Save className="h-3.5 w-3.5 mr-1" /> Save changes
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-500 text-emerald-700"
                        onClick={() => updateOrgMut.mutate({ orgId: selectedOrg.id, updates: { subscription_status: 'exempt', subscription_plan: 'internal' }, auditAction: 'subscription.changed' })}>
                        <Tag className="h-3.5 w-3.5 mr-1" /> Mark exempt
                      </Button>
                      {deact(selectedOrg).deactivated_at ? (
                        <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-500 text-emerald-700"
                          disabled={reactivateOrgMut.isPending} onClick={() => reactivateOrgMut.mutate(selectedOrg.id)}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Reactivate
                        </Button>
                      ) : (
                        <>
                          {selectedOrg.subscription_status === 'suspended' ? (
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => suspendOrgMut.mutate({ orgId: selectedOrg.id, suspend: false })}>
                              <Play className="h-3.5 w-3.5 mr-1" /> Unsuspend
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => suspendOrgMut.mutate({ orgId: selectedOrg.id, suspend: true })}>
                              <Pause className="h-3.5 w-3.5 mr-1" /> Suspend
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-8 text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
                            onClick={() => openOrgAction('deactivate', selectedOrg)}>
                            <UserX className="h-3.5 w-3.5 mr-1" /> Deactivate
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-xs border-destructive text-destructive hover:bg-destructive/10"
                        onClick={() => openOrgAction('delete', selectedOrg)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete org
                      </Button>
                    </div>

                    {/* Stripe info — fetched from org_billing table (owner/super_admin only) */}
                    <div className="border-t pt-3">
                      <OrgBillingInfo orgId={selectedOrg.id} copyToClipboard={copyToClipboard} />
                    </div>
                    </CardContent>
                  </Card>

                  {/* Invites — pending (expired ones stay, they can be resent) or accepted */}
                  <Card>
                    <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                      <div className="flex items-center gap-1" role="tablist" aria-label="Invite status">
                        <button role="tab" aria-selected={orgInviteView === 'open'}
                          className={`text-[13.5px] font-bold px-1.5 py-0.5 rounded ${orgInviteView === 'open' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                          onClick={() => setOrgInviteView('open')}>
                          Pending invites ({orgInvites.open.length})
                        </button>
                        <span className="text-muted-foreground/50 text-xs">·</span>
                        <button role="tab" aria-selected={orgInviteView === 'accepted'}
                          className={`text-[13.5px] font-bold px-1.5 py-0.5 rounded ${orgInviteView === 'accepted' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                          onClick={() => setOrgInviteView('accepted')}>
                          Accepted ({orgInvites.accepted.length})
                        </button>
                      </div>
                      <button className="text-xs font-semibold text-primary hover:underline"
                        title="Invite an agent or admin into this org"
                        onClick={() => openInviteDialog(selectedOrg.id)}>
                        Invite
                      </button>
                    </CardHeader>
                    <CardContent className="px-4 py-1">
                      {orgInviteRows.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-3">
                          {orgInviteView === 'open' ? 'No pending invites.' : 'No accepted invites yet.'}
                        </p>
                      ) : (
                        orgInviteRows.map((inv: any) => {
                          const status = inviteStatus(inv);
                          const busy = resendInviteMut.isPending && resendInviteMut.variables?.id === inv.id;
                          const joined = inv.redeemed_by ? profileMap.get(inv.redeemed_by) : null;
                          return (
                          <div key={inv.id} className="flex items-center gap-3 py-2.5 border-b last:border-b-0">
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold truncate">
                                {inv.email || 'Anyone with the link'}
                                {status === 'expired' && <Badge variant="destructive" className="text-[10px] ml-2 align-middle">Expired</Badge>}
                              </p>
                              <p className="text-[11px] text-muted-foreground">
                                {status === 'accepted'
                                  ? `${String(inv.type ?? '').replace('_', ' ')} invite · accepted ${formatDistanceToNow(new Date(inv.redeemed_at), { addSuffix: true })}${joined ? ` by ${joined.full_name || joined.email}` : ''}`
                                  : `${String(inv.type ?? '').replace('_', ' ')} invite · sent ${formatDistanceToNow(new Date(inv.created_at), { addSuffix: true })} · ${status === 'expired' ? 'expired' : 'expires'} ${formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}`}
                              </p>
                            </div>
                            {status !== 'accepted' && (
                              <div className="flex gap-1 shrink-0">
                                {status === 'pending' && (
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Copy invite link" aria-label="Copy invite link"
                                    onClick={() => copyToClipboard(`${window.location.origin}/invite/${inv.token}`)}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Resend invite (restarts the timer)" aria-label="Resend invite"
                                  disabled={busy}
                                  onClick={() => resendInviteMut.mutate({ id: inv.id, token: inv.token, email: inv.email, org_id: inv.org_id })}>
                                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" title="Revoke invite" aria-label="Revoke invite"
                                  onClick={() => deleteInviteMut.mutate(inv.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>
                  </div>

                  {/* Right: people + activity */}
                  <div className="space-y-4">
                  <Card>
                    <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-[13.5px] font-bold">People in this org</CardTitle>
                      <span className="text-[11px] text-muted-foreground">{orgPeople.length} {orgPeople.length === 1 ? 'person' : 'people'} · super admins never listed</span>
                    </CardHeader>
                    <CardContent className="px-2 py-1">
                      {orgPeople.length === 0 ? (
                        <p className="text-sm text-muted-foreground px-2 py-3">Nobody here yet.</p>
                      ) : (
                        <Table className="[&_td]:py-2">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-[10px] uppercase tracking-wider">Name</TableHead>
                              <TableHead className="text-[10px] uppercase tracking-wider">Email</TableHead>
                              <TableHead className="text-[10px] uppercase tracking-wider">Role</TableHead>
                              <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                              <TableHead className="text-[10px] uppercase tracking-wider">Last seen</TableHead>
                              <TableHead className="text-[10px] uppercase tracking-wider"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {orgPeople.map(p => (
                              <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedUserId(p.id); setActiveTab('users'); }}>
                                <TableCell className="font-semibold text-[13px]">{p.full_name || p.email || 'Unknown'}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{p.email}</TableCell>
                                <TableCell><div className="flex gap-1">{getRoles(p.id, selectedOrg.id).map(r => <RoleBadge key={r} role={r} />)}</div></TableCell>
                                <TableCell><Badge variant={p.status === 'active' ? 'secondary' : 'destructive'} className="text-[10.5px] capitalize">{p.status}</Badge></TableCell>
                                <TableCell className="text-xs text-muted-foreground font-mono">{p.last_sign_in_at ? formatDistanceToNow(new Date(p.last_sign_in_at), { addSuffix: true }) : 'Never'}</TableCell>
                                <TableCell onClick={e => e.stopPropagation()}>
                                  {isSuperAdmin && p.id !== user?.id && (
                                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-violet-400 text-violet-700 hover:bg-violet-50"
                                      disabled={isImpersonating}
                                      onClick={async () => {
                                        try {
                                          await startImpersonation(p.id);
                                          toast.success(`Viewing as ${p.email}`, { description: 'Use the banner at the top to exit.' });
                                        } catch (e: any) {
                                          toast.error('Error', { description: sanitizeError(e) });
                                        }
                                      }}>
                                      <Eye className="h-3 w-3" /> View as
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="px-4 py-3 border-b">
                      <CardTitle className="text-[13.5px] font-bold">Recent activity in this org</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 py-1">
                      {orgActivity.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-3">Nothing yet.</p>
                      ) : (
                        orgActivity.map(log => {
                          const actor = profileMap.get(log.actor_id);
                          const detail = log.details && Object.keys(log.details as object).length > 0
                            ? String((log.details as any).email ?? (log.details as any).scenario_name ?? (log.details as any).client_name ?? (log.details as any).flag ?? '')
                            : '';
                          return (
                            <div key={log.id} className="flex items-center gap-2.5 py-2 border-b last:border-b-0 text-[12.5px]">
                              <Badge variant="outline" className="text-[10.5px] font-mono shrink-0">{log.action}</Badge>
                              <span className="truncate flex-1 text-foreground/80">{[actor?.full_name || actor?.email || 'system', detail].filter(Boolean).join(' · ')}</span>
                              <span className="text-[11px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</span>
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>
                  </div>
                </div>
              </div>
              );
            })() : (
              <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  <h2 className="text-[17px] font-bold tracking-tight">Organizations</h2>
                  <p className="text-xs text-muted-foreground">{customerOrgs.length} org{customerOrgs.length === 1 ? '' : 's'} · the platform root is never listed</p>
                </div>
                <Button size="sm" className="h-8 text-xs gap-1" onClick={() => openInviteDialog('')} title="Invite an agent or admin into any org">
                  <Plus className="h-3.5 w-3.5" /> Invite
                </Button>
              </div>
              <Card>
                <CardContent className="pt-4">
                  {selectedOrgIds.size > 0 && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-muted rounded-lg text-sm">
                      <span className="font-medium">{selectedOrgIds.size} selected</span>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => bulkOrgMut.mutate({ orgIds: [...selectedOrgIds], status: 'active' })}>Activate All</Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => bulkOrgMut.mutate({ orgIds: [...selectedOrgIds], status: 'suspended' })}>Suspend All</Button>
                      <Button size="sm" variant="outline" className="h-7 border-emerald-500 text-emerald-700" onClick={() => bulkOrgMut.mutate({ orgIds: [...selectedOrgIds], status: 'exempt' })}>Mark Exempt</Button>
                      <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => setSelectedOrgIds(new Set())}>Clear</Button>
                    </div>
                  )}
                  <div className="flex gap-2.5 mb-4 items-center flex-wrap">
                    <div className="relative w-[280px]">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input placeholder="Search orgs, owners, emails…" className="pl-8 h-8 text-xs" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                    </div>
                    <div className="flex gap-1">
                      {([['all', 'All'], ['attention', 'Needs attention'], ['active', 'Active'], ['trialing', 'Trialing'], ['exempt', 'Exempt'], ['suspended', 'Suspended'], ['deactivated', `Deactivated (${customerDeactivatedOrgs.length})`]] as const).map(([v, l]) => (
                        <button
                          key={v}
                          onClick={() => setOrgStatusFilter(v)}
                          className={`h-8 px-3 rounded-md border text-xs font-semibold transition-colors ${
                            orgStatusFilter === v
                              ? 'border-primary text-primary bg-primary/5'
                              : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent/50'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Table className="[&_td]:py-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={customerOrgs.length > 0 && customerOrgs.every(o => selectedOrgIds.has(o.id))}
                            onCheckedChange={checked => {
                              if (checked) setSelectedOrgIds(new Set(customerOrgs.map(o => o.id)));
                              else setSelectedOrgIds(new Set());
                            }}
                          />
                        </TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Name</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Level</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Owner</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Plan</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Seats</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Trial Ends</TableHead>
                        <TableHead className="w-28 text-[10px] uppercase tracking-wider"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(orgStatusFilter === 'deactivated' ? customerDeactivatedOrgs : customerOrgs).filter(o => {
                        const q = searchQuery.toLowerCase();
                        const owner = profileMap.get(o.owner_id);
                        const matchSearch = !searchQuery
                          || o.name.toLowerCase().includes(q)
                          || (owner?.full_name ?? '').toLowerCase().includes(q)
                          || (owner?.email ?? '').toLowerCase().includes(q);
                        const matchStatus = orgStatusFilter === 'all' || orgStatusFilter === 'deactivated' ? true
                          : orgStatusFilter === 'attention' ? needsAttentionOrgs.some(n => n.id === o.id)
                          : o.subscription_status === orgStatusFilter;
                        return matchSearch && matchStatus;
                      }).map(o => {
                        const orgUsers = profiles.filter(p => p.org_id === o.id).length;
                        const ownerProfile = profileMap.get(o.owner_id);
                        const levelChip = o.org_type === 'imo'
                          ? 'bg-[#2D4B6E] text-white'
                          : o.org_type === 'agency'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground';
                        return (
                          <TableRow key={o.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedOrgId(o.id)}>
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedOrgIds.has(o.id)}
                                onCheckedChange={checked => {
                                  const next = new Set(selectedOrgIds);
                                  if (checked) next.add(o.id); else next.delete(o.id);
                                  setSelectedOrgIds(next);
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-semibold text-[13px]">{o.name}</TableCell>
                            <TableCell>
                              {o.org_type
                                ? <span className={`inline-block text-[9.5px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${levelChip}`}>{o.org_type}</span>
                                : <span className="text-muted-foreground text-xs">—</span>}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{ownerProfile?.full_name || ownerProfile?.email || 'Unknown'}</TableCell>
                            <TableCell className="text-xs capitalize text-muted-foreground">{o.subscription_plan ?? '—'}</TableCell>
                            <TableCell className="font-mono text-xs tabular-nums">{orgUsers}/{o.max_seats}</TableCell>
                            <TableCell><StatusBadge status={deact(o).deactivated_at ? 'deactivated' : o.subscription_status} /></TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
                              {deact(o).deactivated_at
                                ? <span title="Wiped by the nightly purge">wiped {format(purgeDate(deact(o).deactivated_at!), 'MMM yyyy')}</span>
                                : o.subscription_status === 'trialing' && o.trial_ends_at
                                ? format(new Date(o.trial_ends_at), 'MMM d, yyyy')
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <div onClick={e => e.stopPropagation()}>
                                {deact(o).deactivated_at ? (
                                  <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500 text-emerald-700"
                                    disabled={reactivateOrgMut.isPending} onClick={() => reactivateOrgMut.mutate(o.id)}>
                                    Reactivate
                                  </Button>
                                ) : o.subscription_status === 'suspended' ? (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => suspendOrgMut.mutate({ orgId: o.id, suspend: false })}>
                                    Unsuspend
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => suspendOrgMut.mutate({ orgId: o.id, suspend: true })}>
                                    Suspend
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {customerOrgs.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No organizations yet</p>}
                </CardContent>
              </Card>
              </div>
            )}
          </TabsContent>

          {/* ─── Users ─── */}
          <TabsContent value="users">
            {selectedUserProfile ? (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => setSelectedUserId(null)}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back to Users
                </Button>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedUserProfile.full_name || selectedUserProfile.email || 'Unknown'}</CardTitle>
                    <CardDescription>{selectedUserProfile.email}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><Label className="text-muted-foreground text-xs">Organization</Label><p>{selectedUserProfile.org_id ? orgMap.get(selectedUserProfile.org_id)?.name ?? 'Unknown' : 'None'}</p></div>
                      <div><Label className="text-muted-foreground text-xs">Role(s)</Label><div className="flex gap-1 mt-1">{getRoles(selectedUserProfile.id, selectedUserProfile.org_id).map(r => <RoleBadge key={r} role={r} />)}</div></div>
                      <div><Label className="text-muted-foreground text-xs">Status</Label>
                        <p><Badge variant={selectedUserProfile.status === 'active' ? 'secondary' : selectedUserProfile.status === 'deactivated' ? 'outline' : 'destructive'} className="text-xs capitalize">{selectedUserProfile.status}</Badge></p>
                        {selectedUserProfile.status === 'deactivated' && deact(selectedUserProfile).deactivated_at && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Deactivated {formatDistanceToNow(new Date(deact(selectedUserProfile).deactivated_at!), { addSuffix: true })} {deact(selectedUserProfile).deactivated_by ? 'by an administrator' : 'by themselves'} · wiped {format(purgeDate(deact(selectedUserProfile).deactivated_at!), 'MMM d, yyyy')}
                          </p>
                        )}
                      </div>
                      <div><Label className="text-muted-foreground text-xs">Joined</Label><p>{formatDistanceToNow(new Date(selectedUserProfile.created_at), { addSuffix: true })}</p></div>
                      <div><Label className="text-muted-foreground text-xs">Last Login</Label><p>{selectedUserProfile.last_sign_in_at ? formatDistanceToNow(new Date(selectedUserProfile.last_sign_in_at), { addSuffix: true }) : 'Never'}</p></div>
                    </div>

                    {/* Change Role */}
                    {selectedUserProfile.org_id && getRoles(selectedUserProfile.id, selectedUserProfile.org_id).some(r => r !== 'super_admin') && (
                      <div className="pt-2 border-t">
                        <Label className="text-muted-foreground text-xs">Change Org Role</Label>
                        <div className="flex gap-2 mt-2">
                          {getRoles(selectedUserProfile.id, selectedUserProfile.org_id).filter(r => r !== 'super_admin').map(r => (
                            <Button key={r} size="sm" variant="outline" className="gap-1.5"
                              onClick={() => {
                                setChangeRoleUserId(selectedUserProfile.id);
                                setChangeRoleOrgId(selectedUserProfile.org_id!);
                                setChangeRoleOldRole(r);
                                setChangeRoleNewRole(r);
                                setChangeRoleOpen(true);
                              }}>
                              <RoleBadge role={r} /> Change
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {(() => {
                      const isSelf = selectedUserProfile.id === user?.id;
                      const userRoleList = rolesMap.get(selectedUserProfile.id) ?? [];
                      const isPlatformAdminRow = userRoleList.some(r => r.role === 'platform_admin');
                      const isSuperAdminRow = userRoleList.some(r => r.role === 'super_admin');
                      return (
                        <div className="flex gap-2 pt-2 flex-wrap">
                          {/* Platform Admin grant/revoke — super-admin-only, and never
                              for a super_admin row (you can't demote yourself out of god mode). */}
                          {isSuperAdmin && !isSelf && !isSuperAdminRow && (
                            isPlatformAdminRow ? (
                              <Button size="sm" variant="outline" onClick={() => platformAdminMut.mutate({ userId: selectedUserProfile.id, grant: false })}
                                disabled={platformAdminMut.isPending}>
                                <Shield className="h-3.5 w-3.5 mr-1" /> Revoke Platform Admin
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => platformAdminMut.mutate({ userId: selectedUserProfile.id, grant: true })}
                                disabled={platformAdminMut.isPending}>
                                <Shield className="h-3.5 w-3.5 mr-1" /> Make Platform Admin
                              </Button>
                            )
                          )}
                          {!isSelf && (selectedUserProfile.status === 'deactivated' ? (
                            <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700"
                              disabled={reactivateUserMut.isPending} onClick={() => reactivateUserMut.mutate(selectedUserProfile.id)}>
                              <Play className="h-3.5 w-3.5 mr-1" /> Reactivate
                            </Button>
                          ) : selectedUserProfile.status === 'active' ? (
                            <>
                              <Button size="sm" variant="destructive" onClick={() => updateUserStatusMut.mutate({ userId: selectedUserProfile.id, status: 'suspended' })}>
                                <Pause className="h-3.5 w-3.5 mr-1" /> Suspend
                              </Button>
                              <Button size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10"
                                onClick={() => { setDeactivateUserTarget(selectedUserProfile.id); setDeactivateUserConfirm(''); }}>
                                <UserX className="h-3.5 w-3.5 mr-1" /> Deactivate
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => updateUserStatusMut.mutate({ userId: selectedUserProfile.id, status: 'active' })}>
                              <Play className="h-3.5 w-3.5 mr-1" /> Unsuspend
                            </Button>
                          ))}
                          <Button size="sm" variant="outline" onClick={() => handleExportUserDataReport(selectedUserProfile.id)}>
                            <Download className="h-3.5 w-3.5 mr-1" /> Export Data Report
                          </Button>
                          {/* toggle_diagnostic_mode is hard super-admin-only server-side
                              (batch5_diagnostic) — showing this to platform admins just
                              hands them a guaranteed Forbidden error. */}
                          {isSuperAdmin && (() => {
                            const diagUntil = (selectedUserProfile as any).diagnostic_enabled_until;
                            const diagActive = diagUntil && new Date(diagUntil) > new Date();
                            return (
                              <Button size="sm" variant={diagActive ? 'default' : 'outline'}
                                className={diagActive ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                                onClick={() => toggleDiagnosticMut.mutate({ userId: selectedUserProfile.id, enable: !diagActive })}>
                                {toggleDiagnosticMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ScrollText className="h-3.5 w-3.5 mr-1" />}
                                {diagActive ? 'Diagnostic ON' : 'Enable Diagnostic'}
                              </Button>
                            );
                          })()}
                          {!isSelf && isSuperAdmin && (
                            <Button size="sm" variant="outline" className="border-violet-500 text-violet-700 hover:bg-violet-50"
                              disabled={isImpersonating}
                              onClick={async () => {
                                try {
                                  await startImpersonation(selectedUserProfile.id);
                                  toast.success(`Viewing as ${selectedUserProfile.email}`, { description: "Use the banner at the top to exit." });
                                } catch (e: any) {
                                  toast.error('Error', { description: sanitizeError(e) });
                                }
                              }}>
                              <Shield className="h-3.5 w-3.5 mr-1" /> Impersonate
                            </Button>
                          )}
                          {!isSelf && (
                            <Button size="sm" variant="outline" className="border-destructive text-destructive hover:bg-destructive/10"
                              onClick={() => { setDeleteUserTarget(selectedUserProfile.id); setDeleteUserConfirmEmail(''); setDeleteUserOpen(true); }}>
                              <UserX className="h-3.5 w-3.5 mr-1" /> Delete Account
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  <h2 className="text-[17px] font-bold tracking-tight">Users</h2>
                  <p className="text-xs text-muted-foreground">{profiles.length} people · your own account is never listed</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleExportUsers}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                  </Button>
                  <Button size="sm" className="h-8 text-xs" onClick={() => { setCreateUserOpen(true); setCreateUserEmail(''); setCreateUserName(''); setCreateUserRole('agent'); setCreateUserOrgId(''); }}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Create user
                  </Button>
                </div>
              </div>
              <Card>
                <CardContent className="pt-4">
                  {selectedUserIds.size > 0 && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-muted rounded-lg text-sm">
                      <span className="font-medium">{selectedUserIds.size} selected</span>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => bulkUserMut.mutate({ userIds: [...selectedUserIds], status: 'active' })}>Activate All</Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => bulkUserMut.mutate({ userIds: [...selectedUserIds], status: 'suspended' })}>Suspend All</Button>
                      <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => setSelectedUserIds(new Set())}>Clear</Button>
                    </div>
                  )}
                  <div className="flex gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search by name or email…" className="pl-9 h-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                    </div>
                    <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                      <SelectTrigger className="w-[150px] h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                        <SelectItem value="org_owner">Org Owner</SelectItem>
                        <SelectItem value="org_admin">Org Admin</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                      </SelectContent>
                    </Select>
                    <button type="button" onClick={() => setShowDeactivatedUsers(v => !v)}
                      className={`h-9 px-3 rounded-md border text-xs font-semibold transition-colors ${
                        showDeactivatedUsers ? 'border-primary text-primary bg-primary/5' : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent/50'
                      }`}>
                      Deactivated ({deactivatedProfiles.length})
                    </button>
                  </div>
                  <Table className="[&_td]:py-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={profiles.length > 0 && profiles.filter(p => p.id !== user?.id).every(p => selectedUserIds.has(p.id))}
                            onCheckedChange={checked => {
                              if (checked) setSelectedUserIds(new Set(profiles.filter(p => p.id !== user?.id).map(p => p.id)));
                              else setSelectedUserIds(new Set());
                            }}
                          />
                        </TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Name</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Email</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Organization</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Roles</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Last Login</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(showDeactivatedUsers ? deactivatedProfiles : profiles).filter(p => {
                        if (p.id === user?.id) return false; // hide own account
                        const q = searchQuery.toLowerCase();
                        const matchSearch = !searchQuery || p.full_name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q);
                        const userRoles = getRoles(p.id, p.org_id);
                        const matchRole = userRoleFilter === 'all' || userRoles.includes(userRoleFilter);
                        return matchSearch && matchRole;
                      }).map(p => {
                        const userOrg = p.org_id ? orgMap.get(p.org_id) : null;
                        return (
                          <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedUserId(p.id)}>
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedUserIds.has(p.id)}
                                onCheckedChange={checked => {
                                  const next = new Set(selectedUserIds);
                                  if (checked) next.add(p.id); else next.delete(p.id);
                                  setSelectedUserIds(next);
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{p.full_name || p.email || 'Unknown'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{p.email}</TableCell>
                            <TableCell className="text-sm">{userOrg?.name ?? <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell><div className="flex gap-1 flex-wrap">{getRoles(p.id, p.org_id).map(r => <RoleBadge key={r} role={r} />)}</div></TableCell>
                            <TableCell><Badge variant={p.status === 'active' ? 'secondary' : p.status === 'deactivated' ? 'outline' : 'destructive'} className="text-xs capitalize">{p.status}</Badge></TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {p.status === 'deactivated' && deact(p).deactivated_at
                                ? <span title="Wiped by the nightly purge">wiped {format(purgeDate(deact(p).deactivated_at!), 'MMM yyyy')}</span>
                                : p.last_sign_in_at ? formatDistanceToNow(new Date(p.last_sign_in_at), { addSuffix: true }) : 'Never'}
                            </TableCell>
                            <TableCell onClick={e => e.stopPropagation()}>
                              {p.status === 'deactivated' && (
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-500 text-emerald-700"
                                  disabled={reactivateUserMut.isPending} onClick={() => reactivateUserMut.mutate(p.id)}>
                                  <Play className="h-3 w-3" /> Reactivate
                                </Button>
                              )}
                              {isSuperAdmin && p.status !== 'deactivated' && (
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-violet-400 text-violet-700 hover:bg-violet-50"
                                  disabled={isImpersonating}
                                  onClick={async () => {
                                    try {
                                      await startImpersonation(p.id);
                                      toast.success(`Viewing as ${p.email}`, { description: 'Use the banner at the top to exit.' });
                                    } catch (e: any) {
                                      toast.error('Error', { description: sanitizeError(e) });
                                    }
                                  }}>
                                  <Eye className="h-3 w-3" /> View as
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              </div>
            )}
          </TabsContent>

          {/* ─── Invites ─── */}
          <TabsContent value="invites">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <h2 className="text-[17px] font-bold tracking-tight">Invites</h2>
                <p className="text-xs text-muted-foreground">{pendingInvites} pending · {invites.length} total</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-1" role="tablist" aria-label="Invite status">
                  <Button size="sm" variant={invitesFilter === 'open' ? 'default' : 'ghost'} className="h-8 text-xs"
                    role="tab" aria-selected={invitesFilter === 'open'} onClick={() => setInvitesFilter('open')}>
                    Pending ({invitesPartition.open.length})
                  </Button>
                  <Button size="sm" variant={invitesFilter === 'accepted' ? 'default' : 'ghost'} className="h-8 text-xs"
                    role="tab" aria-selected={invitesFilter === 'accepted'} onClick={() => setInvitesFilter('accepted')}>
                    Accepted ({invitesPartition.accepted.length})
                  </Button>
                </div>
                <Button size="sm" className="h-8 text-xs gap-1" onClick={() => openInviteDialog('')} title="Invite an agent or admin into any org">
                  <Plus className="h-3.5 w-3.5" /> Invite
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="pt-4">
                <Table className="[&_td]:py-2">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px] uppercase tracking-wider">Email</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider">Type</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider">Role</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider">Destination</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider">Created</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider">Expires</TableHead>
                      <TableHead className="w-24 text-[10px] uppercase tracking-wider">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(invitesFilter === 'open' ? invitesPartition.open : invitesPartition.accepted).map(inv => {
                      const status = inviteStatus(inv);
                      const busy = resendInviteMut.isPending && resendInviteMut.variables?.id === inv.id;
                      return (
                        <TableRow key={inv.id}>
                          <TableCell className="text-sm">{inv.email || <span className="text-muted-foreground italic">Any</span>}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs capitalize">{inv.type.replace('_', ' ')}</Badge></TableCell>
                          {/* New Org invites never use the role column (its 'agent' is just the
                              DB default) — the redeemer always becomes org_owner of a new org at
                              the invite's level. Show the level instead of the phantom role. */}
                          <TableCell>
                            {inv.type === 'new_org'
                              ? <Badge variant="default" className="text-xs">{inv.new_org_type ? inv.new_org_type === 'imo' ? 'IMO owner' : `${inv.new_org_type} owner` : 'org owner'}</Badge>
                              : <RoleBadge role={inv.role} />}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{inv.org_id ? orgMap.get(inv.org_id)?.name ?? '—' : '—'}</TableCell>
                          <TableCell><Badge variant={status === 'accepted' ? 'default' : status === 'expired' ? 'destructive' : 'secondary'} className="text-xs capitalize">{status}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(inv.created_at), { addSuffix: true })}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {status !== 'accepted' && (
                                <>
                                  {status === 'pending' && (
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Copy invite link" aria-label="Copy invite link" onClick={() => copyToClipboard(`${window.location.origin}/invite/${inv.token}`)}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Resend invite (restarts the timer)" aria-label="Resend invite" disabled={busy}
                                    onClick={() => resendInviteMut.mutate({ id: inv.id, token: inv.token, email: inv.email, org_id: inv.org_id })}>
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" title="Revoke invite" aria-label="Revoke invite" onClick={() => deleteInviteMut.mutate(inv.id)}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {(invitesFilter === 'open' ? invitesPartition.open : invitesPartition.accepted).length === 0 && (
                  <p className="text-center py-8 text-sm text-muted-foreground">
                    {invitesFilter === 'open' ? 'No pending invites' : 'No accepted invites yet'}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Audit Log ─── */}
          <TabsContent value="audit">
            <Card>
              <CardHeader className="flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-[13.5px] font-bold">Audit log</CardTitle>
                  <p className="text-[11px] text-muted-foreground mt-0.5">last 200 events across all organizations</p>
                </div>
                <Button size="sm" variant="outline" onClick={handleExportAudit}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-4 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search by actor…" className="pl-9 h-9" value={auditSearch} onChange={e => setAuditSearch(e.target.value)} />
                  </div>
                  <Select value={auditActionFilter} onValueChange={setAuditActionFilter}>
                    <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="All actions" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      {auditActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={auditDateFilter} onValueChange={setAuditDateFilter}>
                    <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="24h">Last 24h</SelectItem>
                      <SelectItem value="7d">Last 7 days</SelectItem>
                      <SelectItem value="30d">Last 30 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground h-9" data-testid="audit-show-view-as">
                    <Switch checked={showViewAs} onCheckedChange={setShowViewAs} aria-label="Show View-as" />
                    Show View-as
                  </label>
                </div>
                {filteredAudit.length === 0 ? (
                  <p className="text-center py-8 text-sm text-muted-foreground">No audit events found</p>
                ) : (
                  <Table className="[&_td]:py-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] uppercase tracking-wider">Time</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Actor</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Action</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Target</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Org</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAudit.map(log => {
                        const actor = profileMap.get(log.actor_id);
                        const logOrg = log.org_id ? orgMap.get(log.org_id) : null;
                        return (
                          <TableRow key={log.id}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</TableCell>
                            <TableCell className="text-sm">{actor?.full_name || actor?.email || log.actor_id?.slice(0, 8) || '—'}</TableCell>
                            <TableCell><Badge variant="outline" className="text-xs font-mono">{log.action}</Badge></TableCell>
                            <TableCell className="text-xs text-muted-foreground">{log.target_type && <span>{log.target_type}:{log.target_id?.slice(0, 8)}</span>}</TableCell>
                            <TableCell className="text-sm">{logOrg?.name ?? '—'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-48 truncate">{log.details && Object.keys(log.details as object).length > 0 ? JSON.stringify(log.details) : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Configuration — flags, the announcement banner, and platform
               settings on ONE page. Rail lands on 'flags'; legacy sub-tab values
               are normalized there for super admins. Platform admins get the
               announcements card alone under their own tab value. ─── */}
          <TabsContent value="flags">
            <div className="flex items-baseline gap-2.5 flex-wrap mb-4">
              <h2 className="text-[17px] font-bold tracking-tight">Configuration</h2>
              <p className="text-xs text-muted-foreground">Flags, the announcement banner, and platform settings — one page, no digging.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {/* Feature flags — plain names, the snake_case as a chip */}
              <Card>
                <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-[13.5px] font-bold">Feature flags</CardTitle>
                  {isSuperAdmin && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                      const name = window.prompt('Flag name (snake_case):');
                      if (name) createFlagMut.mutate(name.trim().toLowerCase().replace(/\s+/g, '_'));
                    }}>
                      <Plus className="h-3 w-3 mr-1" /> New flag
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="px-4 py-1">
                  {featureFlags.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">No flags defined</p>
                  ) : (
                    featureFlags.map(f => (
                      <div key={f.id} className="py-3 border-b last:border-b-0 space-y-2.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold">{f.flag_name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</p>
                            <p className="text-[11px] text-muted-foreground">
                              <span className="font-mono bg-muted rounded px-1 py-0.5">{f.flag_name}</span>
                              {f.description ? ` ${f.description}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Global</span>
                            <Switch checked={f.enabled_globally} onCheckedChange={v => toggleFlagMut.mutate({ id: f.id, enabled_globally: v })} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          {/* Only render pins that resolve to a listable org. The platform
                              root (filtered out of fetchOrgs) and deleted orgs never appear
                              — the root must not be an option here in any form. */}
                          {(f.enabled_for_orgs ?? []).filter((orgId: string) => orgMap.has(orgId)).map((orgId: string) => {
                            const o = orgMap.get(orgId);
                            return (
                              <Badge key={orgId} variant="secondary" className="gap-1 text-[10.5px] pr-1">
                                {o?.name ?? orgId.slice(0, 8)}
                                <button className="opacity-60 hover:opacity-100 ml-1" onClick={() => toggleFlagForOrgMut.mutate({ id: f.id, orgId, add: false })}>×</button>
                              </Badge>
                            );
                          })}
                          <Select onValueChange={orgId => orgId && toggleFlagForOrgMut.mutate({ id: f.id, orgId, add: true })}>
                            <SelectTrigger className="h-6 w-32 text-xs"><SelectValue placeholder="+ Pin to org" /></SelectTrigger>
                            <SelectContent>
                              {orgs.filter(o => !(f.enabled_for_orgs ?? []).includes(o.id)).map(o => (
                                <SelectItem key={o.id} value={o.id} className="text-xs">{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          {/* Per-user pins. Super admins are excluded (same `profiles` list as
                              everywhere else) — a super admin only ever sees the product through
                              View-as, and a pin resolves for the impersonated user, not the admin. */}
                          {((f as { enabled_for_users?: string[] }).enabled_for_users ?? []).filter((uid: string) => profileMap.has(uid)).map((uid: string) => {
                            const p = profileMap.get(uid);
                            return (
                              <Badge key={uid} variant="outline" className="gap-1 text-[10.5px] pr-1">
                                {p?.full_name || p?.email || uid.slice(0, 8)}
                                <button className="opacity-60 hover:opacity-100 ml-1" onClick={() => toggleFlagForUserMut.mutate({ id: f.id, userId: uid, add: false })}>×</button>
                              </Badge>
                            );
                          })}
                          <Select onValueChange={uid => uid && toggleFlagForUserMut.mutate({ id: f.id, userId: uid, add: true })}>
                            <SelectTrigger className="h-6 w-32 text-xs"><SelectValue placeholder="+ Pin to user" /></SelectTrigger>
                            <SelectContent>
                              {profiles.filter(p => !((f as { enabled_for_users?: string[] }).enabled_for_users ?? []).includes(p.id)).map(p => (
                                <SelectItem key={p.id} value={p.id} className="text-xs">{p.full_name || p.email}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              <div className="space-y-4">
                {/* Announcement banner — composer + history in one card */}
                <Card>
                  <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-[13.5px] font-bold">Announcement banner</CardTitle>
                    <span className="text-[11px] text-muted-foreground">shown to every signed-in user</span>
                  </CardHeader>
                  <CardContent className="px-4 py-3 space-y-3">
                    <div className="flex gap-2">
                      <Input className="h-9 flex-1" value={announcementMsg} onChange={e => setAnnouncementMsg(e.target.value)} placeholder="System maintenance scheduled for…" />
                      <Select value={announcementType} onValueChange={(v: any) => setAnnouncementType(v)}>
                        <SelectTrigger className="h-9 w-[110px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Info</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="h-9" disabled={!announcementMsg || createAnnouncementMut.isPending} onClick={() => createAnnouncementMut.mutate({ message: announcementMsg, type: announcementType })}>
                        {createAnnouncementMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Publish'}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Publishing a new announcement deactivates the previous one.</p>
                    {announcements.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">No announcements yet</p>
                    ) : (
                      <div>
                        {announcements.map(a => (
                          <div key={a.id} className="flex items-center gap-2.5 py-2 border-t text-[12.5px]">
                            <Badge variant="outline" className="text-[10.5px] capitalize shrink-0">{a.type}</Badge>
                            <span className="truncate flex-1">{a.message}</span>
                            <span className="text-[11px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</span>
                            <Switch checked={a.active} onCheckedChange={v => toggleAnnouncementMut.mutate({ id: a.id, active: v })} />
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteAnnouncementMut.mutate(a.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Platform settings */}
                <Card>
                  <CardHeader className="px-4 py-3 border-b">
                    <CardTitle className="text-[13.5px] font-bold">Platform settings</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 py-1">
                    {settings.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">No settings found</p>
                    ) : (
                      <>
                        {settings.filter(s => isSuperAdmin || s.key !== 'maintenance_mode').map(s => {
                          const isBool = s.value === true || s.value === false || s.value === 'true' || s.value === 'false';
                          const currentRaw = getSettingValue(s.key);
                          const boolVal = currentRaw === 'true';
                          return (
                            <div key={s.key} className="flex items-center justify-between gap-3 py-2.5 border-b last:border-b-0">
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold">{s.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                                <p className="text-[11px] text-muted-foreground">Updated {s.updated_at ? formatDistanceToNow(new Date(s.updated_at), { addSuffix: true }) : '—'}</p>
                              </div>
                              {isBool ? (
                                <Switch
                                  checked={boolVal}
                                  onCheckedChange={checked => setEditedSettings(prev => ({ ...prev, [s.key]: String(checked) }))}
                                />
                              ) : (
                                <Input
                                  className="w-28 h-8 text-right font-mono text-xs"
                                  value={currentRaw.replace(/^"|"$/g, '')}
                                  onChange={e => setEditedSettings(prev => ({ ...prev, [s.key]: JSON.stringify(e.target.value) }))}
                                />
                              )}
                            </div>
                          );
                        })}
                        <div className="py-3">
                          <Button size="sm" className="h-8 text-xs" onClick={handleSaveSettings} disabled={savingSettings || Object.keys(editedSettings).length === 0}>
                            {savingSettings ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                            Save settings
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ─── Announcements (platform admins — their whole Configuration) ─── */}
          {!isSuperAdmin && (
          <TabsContent value="announcements">
            <div className="flex items-baseline gap-2.5 flex-wrap mb-4">
              <h2 className="text-[17px] font-bold tracking-tight">Configuration</h2>
              <p className="text-xs text-muted-foreground">The announcement banner shown to every signed-in user.</p>
            </div>
            <Card className="max-w-2xl">
              <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
                <CardTitle className="text-[13.5px] font-bold">Announcement banner</CardTitle>
                <span className="text-[11px] text-muted-foreground">publishing a new one deactivates the previous</span>
              </CardHeader>
              <CardContent className="px-4 py-3 space-y-3">
                <div className="flex gap-2">
                  <Input className="h-9 flex-1" value={announcementMsg} onChange={e => setAnnouncementMsg(e.target.value)} placeholder="System maintenance scheduled for…" />
                  <Select value={announcementType} onValueChange={(v: any) => setAnnouncementType(v)}>
                    <SelectTrigger className="h-9 w-[110px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="h-9" disabled={!announcementMsg || createAnnouncementMut.isPending} onClick={() => createAnnouncementMut.mutate({ message: announcementMsg, type: announcementType })}>
                    {createAnnouncementMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Publish'}
                  </Button>
                </div>
                {announcements.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No announcements yet</p>
                ) : (
                  <div>
                    {announcements.map(a => (
                      <div key={a.id} className="flex items-center gap-2.5 py-2 border-t text-[12.5px]">
                        <Badge variant="outline" className="text-[10.5px] capitalize shrink-0">{a.type}</Badge>
                        <span className="truncate flex-1">{a.message}</span>
                        <span className="text-[11px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</span>
                        <Switch checked={a.active} onCheckedChange={v => toggleAnnouncementMut.mutate({ id: a.id, active: v })} />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteAnnouncementMut.mutate(a.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}



          {/* ─── Revenue ─── */}
          <TabsContent value="revenue">
            {(() => {
              const PLAN_PRICES: Record<string, number> = { personal: 29, standard: 49, team: 99, enterprise: 299, partner: 0, internal: 0 };
              const activeOrgs = orgs.filter(o => o.subscription_status === 'active');
              const trialingOrgs = orgs.filter(o => o.subscription_status === 'trialing');
              const canceledLast30 = orgs.filter(o => o.subscription_status === 'canceled' && new Date(o.updated_at ?? o.created_at) > subDays(new Date(), 30));
              const mrr = activeOrgs.reduce((sum, o) => sum + (PLAN_PRICES[o.subscription_plan ?? 'standard'] ?? 49), 0);

              const trialConversionRate = trialingOrgs.length > 0
                ? Math.round((activeOrgs.length / (activeOrgs.length + trialingOrgs.length)) * 100)
                : 0;
              const churnRate = activeOrgs.length > 0 ? Math.round((canceledLast30.length / activeOrgs.length) * 100) : 0;

              // New signups per week (last 8 weeks)
              const now = new Date();
              const weekStart = subDays(now, 56);
              const weeks = eachWeekOfInterval({ start: weekStart, end: now });
              const signupsByWeek = weeks.map(w => {
                const weekEnd = new Date(w.getTime() + 7 * 86400000);
                const count = orgs.filter(o => new Date(o.created_at) >= w && new Date(o.created_at) < weekEnd).length;
                return { week: format(w, 'MMM d'), signups: count };
              });

              // Status breakdown
              const statusData = Object.entries(orgsByStatus).map(([status, count]) => ({ name: status, value: count }));
              const PIE_COLORS = ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6','#10b981'];

              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'MRR', value: `$${mrr.toLocaleString()}`, sub: `${activeOrgs.length} active orgs` },
                      { label: 'Active Subscriptions', value: activeOrgs.length, sub: 'paying orgs' },
                      { label: 'Trial Conversion', value: `${trialConversionRate}%`, sub: 'trialing → active' },
                      { label: 'Churn (30d)', value: `${churnRate}%`, sub: `${canceledLast30.length} canceled` },
                    ].map(s => (
                      <Card key={s.label}>
                        <CardContent className="px-3.5 py-2.5">
                          <p className="font-mono text-xl font-semibold leading-tight tabular-nums">{s.value}</p>
                          <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">{s.label}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="px-4 py-3 border-b"><CardTitle className="text-[13.5px] font-bold">New signups per week</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={signupsByWeek}>
                            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <ChartTooltip />
                            <Bar dataKey="signups" fill="#3b82f6" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="px-4 py-3 border-b"><CardTitle className="text-[13.5px] font-bold">Subscription status breakdown</CardTitle></CardHeader>
                      <CardContent className="flex items-center justify-center">
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                              {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <ChartTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0"><CardTitle className="text-[13.5px] font-bold">Recent logins</CardTitle><span className="text-[11px] text-muted-foreground">last 20 sign-ins</span></CardHeader>
                    <CardContent>
                      <Table className="[&_td]:py-2">
                        <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-wider">User</TableHead><TableHead className="text-[10px] uppercase tracking-wider">Email</TableHead><TableHead className="text-[10px] uppercase tracking-wider">Org</TableHead><TableHead className="text-[10px] uppercase tracking-wider">Last Login</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {profiles.filter(p => p.last_sign_in_at).sort((a,b) => new Date(b.last_sign_in_at!).getTime() - new Date(a.last_sign_in_at!).getTime()).slice(0, 20).map(p => (
                            <TableRow key={p.id}>
                              <TableCell className="font-medium text-sm">{p.full_name || p.email}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{p.email}</TableCell>
                              <TableCell className="text-sm">{p.org_id ? orgMap.get(p.org_id)?.name ?? '—' : '—'}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{p.last_sign_in_at ? formatDistanceToNow(new Date(p.last_sign_in_at), { addSuffix: true }) : 'Never'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}
          </TabsContent>

          {/* ─── Errors ─── */}
          <TabsContent value="errors">
            {(() => {
              const filtered = errorLog.filter(e =>
                (errorTypeFilter === 'all' || e.error_type === errorTypeFilter) &&
                (errorShowAcked || !e.acknowledged)
              );
              // Bundle repeats: one row per signature (type + message), so a
              // sign-out that fired 14 times over three weeks reads as ×14
              // with a first-seen date — not 14 identical rows. errorLog is
              // newest-first, so each bundle's [0] is its latest instance.
              const groupMap = new Map<string, typeof filtered>();
              for (const e of filtered) {
                const key = `${e.error_type}|${e.error_message}`;
                const arr = groupMap.get(key) ?? [];
                arr.push(e);
                groupMap.set(key, arr);
              }
              const groups = [...groupMap.values()];
              const selectedGroup = groups.find(g => g.some(e => e.id === expandedErrorId)) ?? groups[0] ?? null;
              const selectedError = selectedGroup?.[0] ?? null;
              const groupTotal = (g: typeof filtered) => g.reduce((s, e) => s + (e.occurrence_count ?? 1), 0);
              const selCount = selectedGroup ? groupTotal(selectedGroup) : 0;
              const selFirstSeen = selectedGroup ? selectedGroup[selectedGroup.length - 1].created_at : null;
              const selWho = selectedGroup ? describeWho(selectedGroup, id => profileMap.get(id)) : '';
              const selUserAgent = (selectedError?.auth_diagnostics as { userAgent?: string } | null)?.userAgent ?? null;
              const selUnacked = selectedGroup ? selectedGroup.filter(e => !e.acknowledged) : [];
              const diagnosisEntry = selectedError ? {
                error_message: selectedError.error_message,
                http_status: selectedError.http_status,
                query_name: selectedError.query_name,
                error_type: selectedError.error_type,
                stack_trace: selectedError.stack_trace,
                page: selectedError.page,
                auth_diagnostics: selectedError.auth_diagnostics,
                source: selectedError.source,
                occurrence_count: selectedError.occurrence_count,
              } : null;
              const diagnosis = diagnosisEntry ? diagnoseError(diagnosisEntry) : null;
              const shownAuth = diagnosisEntry && diagnosis ? authDiagnosticsFor(diagnosisEntry, diagnosis) : null;
              const severityColor = diagnosis?.severity === 'critical' ? 'text-red-600' : diagnosis?.severity === 'error' ? 'text-orange-600' : 'text-yellow-600';
              const su = selectedError ? profileMap.get(selectedError.user_id) : null;
              return (
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                      <h2 className="text-[17px] font-bold tracking-tight">Errors</h2>
                      <p className="text-xs text-muted-foreground">{errorLog.filter(e => !e.acknowledged).length} unreviewed · newest first · select one to read it</p>
                    </div>
                    <div className="flex gap-2 flex-wrap items-center">
                      <Select value={errorTypeFilter} onValueChange={setErrorTypeFilter}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All types</SelectItem>
                          <SelectItem value="crash">Crash</SelectItem>
                          <SelectItem value="query">Query</SelectItem>
                          <SelectItem value="rpc">RPC</SelectItem>
                          <SelectItem value="auth">Auth</SelectItem>
                          <SelectItem value="diagnostic">Diagnostic</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1.5 text-xs">
                        <Switch checked={errorShowAcked} onCheckedChange={setErrorShowAcked} />
                        <span className="text-muted-foreground">Show reviewed</span>
                      </div>
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => acknowledgeAllErrorsMut.mutate()}>
                        Mark all reviewed
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => refetchErrors()}>
                        <Search className="h-3.5 w-3.5 mr-1" /> Refresh
                      </Button>
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No errors found</CardContent></Card>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[360px,1fr] gap-4 items-start">
                      {/* List — one row per signature. On a wide screen it scrolls inside its own
                          box and stays pinned under the header, so picking a row far down never
                          drags the page away from the detail card. The box ends well inside the
                          window (the page heading sits above it) and its scroll never chains into
                          the page, so reaching either end of the list moves nothing else
                          (Drew, 2026-10-30: "that needs to be its own standalone box"). */}
                      <Card className="overflow-hidden lg:sticky lg:top-24 lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto lg:overscroll-contain">
                        {groups.map(g => {
                          const e = g[0];
                          const isSelected = selectedGroup === g;
                          const count = groupTotal(g);
                          const who = describeWho(g, id => profileMap.get(id));
                          const allAcked = g.every(x => x.acknowledged);
                          return (
                            <div
                              key={e.id}
                              onClick={() => setExpandedErrorId(e.id)}
                              className={`px-3.5 py-2.5 cursor-pointer border-b last:border-b-0 transition-colors ${
                                isSelected ? 'bg-primary/5 border-l-[3px] border-l-primary' : 'border-l-[3px] border-l-transparent hover:bg-accent/30'
                              } ${allAcked ? 'opacity-55' : ''}`}
                            >
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px] capitalize shrink-0">{e.error_type}</Badge>
                                <p className="text-[12.5px] font-semibold truncate flex-1">{e.error_message}</p>
                                {count > 1 && <span className="font-mono text-[11px] font-semibold text-destructive shrink-0">×{count}</span>}
                                {allAcked
                                  ? <Badge variant="secondary" className="text-[10px] shrink-0">Reviewed</Badge>
                                  : <Badge variant="destructive" className="text-[10px] shrink-0">New</Badge>}
                              </div>
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                {[who,
                                  e.page,
                                  `latest ${formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}`].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          );
                        })}
                      </Card>

                      {/* Reading pane */}
                      {selectedError && diagnosis && (
                        <Card>
                          <CardHeader className="px-4 py-3 border-b flex-row items-start justify-between space-y-0 gap-3">
                            <div className="flex items-start gap-2.5 min-w-0">
                              <div className={`mt-0.5 shrink-0 ${severityColor}`}>
                                {diagnosis.severity === 'critical' ? <Bug className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                              </div>
                              <div className="min-w-0">
                                <CardTitle className={`text-[13.5px] font-bold leading-snug ${severityColor}`}>{diagnosis.title}</CardTitle>
                                <Badge variant="outline" className="text-[10px] capitalize mt-1">{diagnosis.severity}</Badge>
                              </div>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                              <Button
                                size="sm" variant="outline" className="h-7 text-xs gap-1"
                                onClick={() => {
                                  const details = [
                                    `Diagnosis: ${diagnosis.title}`,
                                    `Severity: ${diagnosis.severity}`,
                                    `Description: ${diagnosis.description}`,
                                    diagnosis.suggestedFix ? `Suggested Fix: ${diagnosis.suggestedFix}` : '',
                                    '',
                                    `Error: ${selectedError.error_message}`,
                                    `Type: ${selectedError.error_type}`,
                                    selectedError.query_name ? `Query: ${selectedError.query_name}` : '',
                                    selectedError.http_status ? `HTTP Status: ${selectedError.http_status}` : '',
                                    selectedError.page ? `Page: ${selectedError.page}` : '',
                                    `User: ${su?.email || selectedError.user_id || 'anon'}`,
                                    `Time: ${format(new Date(selectedError.created_at), 'PPpp')}`,
                                    selectedError.stack_trace ? `\nStack Trace:\n${selectedError.stack_trace}` : '',
                                    selectedError.input_data ? `\nInput Data:\n${typeof selectedError.input_data === 'string' ? selectedError.input_data : JSON.stringify(selectedError.input_data, null, 2)}` : '',
                                  ].filter(Boolean).join('\n');
                                  navigator.clipboard.writeText(details);
                                  toast.success('Copied to clipboard');
                                }}
                              >
                                <Clipboard className="h-3 w-3" /> Copy all
                              </Button>
                              {isSuperAdmin && selectedError.user_id && selectedError.user_id !== user?.id && (
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-violet-400 text-violet-700 hover:bg-violet-50"
                                  disabled={isImpersonating}
                                  onClick={async () => {
                                    try {
                                      await startImpersonation(selectedError.user_id);
                                      toast.success('Viewing as user', { description: 'Use the banner at the top to exit.' });
                                    } catch (err: any) {
                                      toast.error('Error', { description: sanitizeError(err) });
                                    }
                                  }}>
                                  <Eye className="h-3 w-3" /> View as
                                </Button>
                              )}
                              {selUnacked.length > 0 && (
                                <Button size="sm" className="h-7 text-xs" onClick={() => selUnacked.forEach(e => acknowledgeErrorMut.mutate(e.id))}>
                                  Acknowledge{selUnacked.length > 1 ? ` (${selUnacked.length})` : ''}
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="px-4 py-4 space-y-4">
                            {/* Facts strip — occurrence math covers the whole bundle */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Who</p><p className="text-xs font-medium truncate" title={su?.email ?? undefined}>{selWho}</p></div>
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">First seen</p><p className="text-xs font-mono">{selFirstSeen ? format(new Date(selFirstSeen), 'PPp') : '—'}</p></div>
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Last seen</p><p className="text-xs font-mono">{format(new Date(selectedError.created_at), 'PPp')}</p></div>
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Occurrences</p><p className="text-xs font-mono">×{selCount}</p></div>
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Session</p><p className="text-xs font-medium">{sessionLabel(selectedError)}</p></div>
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Browser</p><p className="text-xs font-medium truncate" title={selUserAgent ?? undefined}>{describeUserAgent(selUserAgent)}</p></div>
                              <div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Delivered via</p><p className="text-xs font-mono truncate">{deliveryLabel(selectedError.source)}</p></div>
                              {selectedError.query_name && (<div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Query</p><p className="text-xs font-mono truncate">{selectedError.query_name}</p></div>)}
                              {selectedError.http_status && (<div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">HTTP</p><p className="text-xs font-mono">{selectedError.http_status}</p></div>)}
                              {selectedError.page && (<div><p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Page</p><p className="text-xs font-mono truncate">{selectedError.page}</p></div>)}
                            </div>

                            {/* What happened */}
                            <div className="space-y-1">
                              <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">What happened</p>
                              <p className="text-sm">{diagnosis.description}</p>
                            </div>

                            {/* Suggested fix */}
                            {diagnosis.suggestedFix && (
                            <div className="space-y-1">
                              <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1">
                                <Wrench className="h-3 w-3" /> Suggested fix
                              </p>
                              <p className="text-sm bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-md px-3 py-2">{diagnosis.suggestedFix}</p>
                            </div>
                            )}

                            {/* Full error message */}
                            <div className="space-y-1">
                              <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Full error message</p>
                              <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-32 whitespace-pre-wrap break-words font-mono">{selectedError.error_message}</pre>
                            </div>

                            {/* Stack trace */}
                            {selectedError.stack_trace && (
                              <Collapsible>
                                <CollapsibleTrigger className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors">
                                  <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" /> Stack trace
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-48 whitespace-pre-wrap break-words font-mono mt-1">{selectedError.stack_trace}</pre>
                                </CollapsibleContent>
                              </Collapsible>
                            )}

                            {/* Input data */}
                            {selectedError.input_data && (
                              <Collapsible>
                                <CollapsibleTrigger className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors">
                                  <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" /> Input data
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-48 whitespace-pre-wrap break-words font-mono mt-1">{typeof selectedError.input_data === 'string' ? selectedError.input_data : JSON.stringify(selectedError.input_data, null, 2)}</pre>
                                </CollapsibleContent>
                              </Collapsible>
                            )}

                            {/* Auth diagnostics */}
                            {shownAuth && (
                              <Collapsible>
                                <CollapsibleTrigger className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors">
                                  <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" /> Auth diagnostics
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-48 whitespace-pre-wrap break-words font-mono mt-1">{JSON.stringify(shownAuth, null, 2)}</pre>
                                </CollapsibleContent>
                              </Collapsible>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* ─── Support — reading pane: tickets left, thread right ─── */}
          <TabsContent value="support">
            {(() => {
              const statusTickets = supportTickets.filter((t: any) =>
                ticketStatusFilter === 'all' ? true : t.status === ticketStatusFilter
              );
              // Counts follow the status filter, so "Export · 17" means 17 open
              // export tickets when Open is picked (Drew, 2026-11-04).
              const sectionOptions = sectionCounts(statusTickets);
              const filteredTickets = filterBySection(statusTickets, ticketSectionFilter);
              const selectedTicket = (selectedTicketId ? supportTickets.find((t: any) => t.id === selectedTicketId) : null) ?? filteredTickets[0] ?? null;
              const CATEGORY_COLORS: Record<string, string> = {
                bug: 'destructive',
                calculation: 'default',
                question: 'secondary',
                comment: 'outline',
              };
              const ticketUser = selectedTicket ? profileMap.get(selectedTicket.user_id) : null;
              const ticketOrg = selectedTicket?.org_id ? orgMap.get(selectedTicket.org_id) : null;
              const isDiagnosticActive = !!(ticketUser?.diagnostic_enabled_until && new Date(ticketUser.diagnostic_enabled_until) > new Date());
              const ticketCreated = selectedTicket ? new Date(selectedTicket.created_at).getTime() : 0;
              const linkedErrors = selectedTicket?.category === 'bug'
                ? errorLog.filter(e =>
                    e.user_id === selectedTicket.user_id &&
                    Math.abs(new Date(e.created_at).getTime() - ticketCreated) < 86400000
                  ).slice(0, 10)
                : [];
              return (
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                      <h2 className="text-[17px] font-bold tracking-tight">Support</h2>
                      <p className="text-xs text-muted-foreground">{supportTickets.filter((t: any) => t.status === 'open').length} open · select a ticket to read it</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={ticketSectionFilter} onValueChange={setTicketSectionFilter}>
                        <SelectTrigger className="w-[220px] h-8 text-xs" data-testid="support-section-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All sections · {statusTickets.length}</SelectItem>
                          {sectionOptions.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label} · {o.count}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                        <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All tickets</SelectItem>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {filteredTickets.length === 0 ? (
                    <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No tickets found</CardContent></Card>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-4 items-start">
                      {/* Ticket list */}
                      <Card className="overflow-hidden">
                        {filteredTickets.map((t: any) => {
                          const tUser = profileMap.get(t.user_id);
                          const isSelected = selectedTicket?.id === t.id;
                          return (
                            <div
                              key={t.id}
                              onClick={() => setSelectedTicketId(t.id)}
                              className={`px-3.5 py-2.5 cursor-pointer border-b last:border-b-0 transition-colors ${
                                isSelected ? 'bg-primary/5 border-l-[3px] border-l-primary' : 'border-l-[3px] border-l-transparent hover:bg-accent/30'
                              } ${t.status === 'resolved' ? 'opacity-55' : ''}`}
                            >
                              <div className="flex items-center gap-2">
                                <Badge variant={t.status === 'open' ? 'destructive' : t.status === 'in_progress' ? 'secondary' : 'outline'} className="text-[10px] capitalize shrink-0">{t.status.replace('_', ' ')}</Badge>
                                <p className="text-[12.5px] font-semibold truncate flex-1">{t.subject}</p>
                                {awaitingSupport(t) && <span className="h-2 w-2 rounded-full bg-primary shrink-0" aria-label="New message from them" data-testid="ticket-awaiting-support" />}
                              </div>
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                {[tUser?.full_name || tUser?.email || 'Unknown', supportCategoryLabel(t.category), supportSectionLabel(t.section, t.section_detail),
                                  formatDistanceToNow(new Date(t.created_at), { addSuffix: true })].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          );
                        })}
                      </Card>

                      {/* Reading pane */}
                      {selectedTicket && (
                        <Card>
                          <CardHeader className="px-4 py-3 border-b flex-row items-start justify-between space-y-0 gap-3">
                            <div className="min-w-0">
                              <CardTitle className="text-[13.5px] font-bold leading-snug">{selectedTicket.subject}</CardTitle>
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                From {ticketUser?.full_name || ticketUser?.email || 'Unknown'} · {ticketOrg?.name ?? 'No org'} · {formatDistanceToNow(new Date(selectedTicket.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {isDiagnosticActive && (
                                <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600">
                                  <Bug className="h-3 w-3 mr-1" /> Diagnostics active
                                </Badge>
                              )}
                              <Badge variant={CATEGORY_COLORS[selectedTicket.category] as any ?? 'outline'} className="text-[10px]">
                                {supportCategoryLabel(selectedTicket.category)}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]" data-testid="support-ticket-section">
                                {supportSectionLabel(selectedTicket.section, selectedTicket.section_detail)}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="px-4 py-4 space-y-4">
                            <div className="bg-muted/30 rounded-lg p-3.5 text-sm whitespace-pre-wrap">
                              {selectedTicket.message}
                            </div>

                            <SupportReplyBox key={selectedTicket.id} ticket={selectedTicket} />

                            {/* Actions strip */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <Select
                                value={selectedTicket.status}
                                onValueChange={v => updateTicketMut.mutate({
                                  ticketId: selectedTicket.id,
                                  updates: {
                                    status: v,
                                    ...(v === 'resolved' ? { resolved_at: new Date().toISOString() } : { resolved_at: null }),
                                  },
                                })}
                              >
                                <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="open">Open</SelectItem>
                                  <SelectItem value="in_progress">In progress</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                </SelectContent>
                              </Select>
                              {isSuperAdmin && !isDiagnosticActive && (
                                <Button
                                  variant="outline" size="sm" className="h-8 text-xs"
                                  onClick={() => toggleDiagnosticMut.mutate({ userId: selectedTicket.user_id, enable: true })}
                                  disabled={toggleDiagnosticMut.isPending}
                                >
                                  {toggleDiagnosticMut.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Bug className="h-3 w-3 mr-1" />}
                                  Enable diagnostics
                                </Button>
                              )}
                              {isSuperAdmin && (
                                <Button
                                  variant="outline" size="sm" className="h-8 text-xs gap-1 border-violet-400 text-violet-700 hover:bg-violet-50"
                                  disabled={isImpersonating}
                                  onClick={async () => {
                                    try {
                                      await startImpersonation(selectedTicket.user_id);
                                      toast.success('Viewing as user', { description: 'Use the banner at the top to exit.' });
                                    } catch (e: any) {
                                      toast.error('Error', { description: sanitizeError(e) });
                                    }
                                  }}
                                >
                                  <Eye className="h-3 w-3" /> View as user
                                </Button>
                              )}
                              <span className="text-[11px] text-muted-foreground ml-auto">{ticketUser?.email ?? ''}</span>
                            </div>

                            {/* Auto-enriched error logs for bug tickets */}
                            {selectedTicket.category === 'bug' && (
                              <div className="space-y-2.5">
                                <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1">
                                  <Wrench className="h-3 w-3" /> Auto-linked errors ({linkedErrors.length})
                                  {linkedErrors.length === 0 && <span className="normal-case tracking-normal font-normal">— none within 24h of this ticket</span>}
                                </p>
                                {linkedErrors.map((err: any) => {
                                  const diagnosis = diagnoseError(err);
                                  const sevColors: Record<string, string> = {
                                    critical: 'border-destructive/50 bg-destructive/5',
                                    error: 'border-orange-500/30 bg-orange-500/5',
                                    warning: 'border-amber-500/30 bg-amber-500/5',
                                  };
                                  return (
                                    <div key={err.id} className={`rounded-lg border p-3 space-y-2 ${sevColors[diagnosis.severity] ?? ''}`}>
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <Badge variant={diagnosis.severity === 'critical' ? 'destructive' : 'secondary'} className="text-[10px]">
                                            {diagnosis.severity.toUpperCase()}
                                          </Badge>
                                          <span className="text-sm font-medium">{diagnosis.title}</span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground">
                                          {formatDistanceToNow(new Date(err.created_at), { addSuffix: true })}
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground">{diagnosis.description}</p>
                                      {diagnosis.suggestedFix && (
                                      <div className="rounded bg-muted/50 p-2">
                                        <p className="text-xs font-medium text-primary">Suggested fix</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">{diagnosis.suggestedFix}</p>
                                      </div>
                                      )}
                                      <p className="text-[10px] text-muted-foreground font-mono truncate">{err.error_message}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Admin notes */}
                            <div className="space-y-1.5">
                              <p className="text-[10.5px] font-semibold tracking-wide uppercase text-muted-foreground">Admin notes (internal)</p>
                              <Textarea
                                key={selectedTicket.id}
                                className="text-sm"
                                rows={3}
                                defaultValue={selectedTicket.admin_notes ?? ''}
                                placeholder="Add internal notes…"
                                onBlur={(e) => {
                                  if (e.target.value !== (selectedTicket.admin_notes ?? '')) {
                                    updateTicketMut.mutate({ ticketId: selectedTicket.id, updates: { admin_notes: e.target.value } });
                                  }
                                }}
                              />
                            </div>

                            {selectedTicket.resolved_at && (
                              <p className="text-xs text-muted-foreground">Resolved {formatDistanceToNow(new Date(selectedTicket.resolved_at), { addSuffix: true })}</p>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

            </div>
          </div>
        </Tabs>
      </main>
      </div>

      {/* ─── Change Role Dialog ─── */}
      <Dialog open={changeRoleOpen} onOpenChange={open => { setChangeRoleOpen(open); if (!open) { setChangeRoleUserId(null); setChangeRoleNewRole(''); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Change Role</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-muted-foreground text-xs">User</Label>
              <p className="text-sm font-medium">{changeRoleUserId ? (profileMap.get(changeRoleUserId)?.full_name || profileMap.get(changeRoleUserId)?.email) : ''}</p>
            </div>
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select value={changeRoleNewRole} onValueChange={setChangeRoleNewRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                  <SelectItem value="org_owner">Org Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeRoleOpen(false)}>Cancel</Button>
            <Button
              disabled={changeUserRoleMut.isPending || changeRoleNewRole === changeRoleOldRole || !changeRoleNewRole}
              onClick={() => changeUserRoleMut.mutate({
                userId: changeRoleUserId!,
                orgId: changeRoleOrgId!,
                oldRole: changeRoleOldRole,
                newRole: changeRoleNewRole,
              })}>
              {changeUserRoleMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── Org-hub Invite Dialog ─── */}
      <Dialog open={orgInviteOpen} onOpenChange={setOrgInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Invite to {orgMap.get(orgInviteOrgId)?.name ?? 'an organization'}</DialogTitle></DialogHeader>
          {!orgInviteLink ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                They join the org with the role you pick. Leave the email blank for an open link anyone can redeem.
                New IMOs, agencies, and teams are still created from the Network tree.
              </p>
              <div className="space-y-2">
                <Label>Organization <span className="text-destructive">*</span></Label>
                <Select value={orgInviteOrgId} onValueChange={setOrgInviteOrgId}>
                  <SelectTrigger><SelectValue placeholder="Pick an organization" /></SelectTrigger>
                  <SelectContent>
                    {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Email (optional)</Label>
                <Input type="email" value={orgInviteEmail} onChange={e => setOrgInviteEmail(e.target.value)} placeholder="agent@example.com" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={orgInviteRole} onValueChange={v => setOrgInviteRole(v as 'agent' | 'org_admin')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="org_admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">Send this link to the new member:</p>
              <div className="flex gap-2">
                <Input value={orgInviteLink} readOnly className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(orgInviteLink)}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!orgInviteLink ? (
              <>
                <Button variant="outline" onClick={() => setOrgInviteOpen(false)}>Cancel</Button>
                <Button disabled={orgInviteMut.isPending || !orgInviteOrgId}
                  onClick={() => { const o = orgMap.get(orgInviteOrgId); if (o) orgInviteMut.mutate({ orgId: o.id, orgName: o.name }); }}>
                  {orgInviteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
                  Send Invite
                </Button>
              </>
            ) : (
              <Button onClick={() => setOrgInviteOpen(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── Create User Dialog ─── */}
      <Dialog open={createUserOpen} onOpenChange={open => { setCreateUserOpen(open); if (!open) { setCreateUserEmail(''); setCreateUserName(''); setCreateUserRole('agent'); setCreateUserOrgId('none'); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Create User Account</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Creates an auth account and sends a password-setup email. If no org is selected, a personal org is created automatically.
            </p>
            <div className="space-y-2">
              <Label>Email <span className="text-destructive">*</span></Label>
              <Input type="email" value={createUserEmail} onChange={e => setCreateUserEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={createUserName} onChange={e => setCreateUserName(e.target.value)} placeholder="Jane Smith" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={createUserRole} onValueChange={setCreateUserRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                  <SelectItem value="org_owner">Org Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign to Org <span className="text-muted-foreground text-xs">(optional — leave blank to auto-create)</span></Label>
              <Select value={createUserOrgId} onValueChange={setCreateUserOrgId}>
                <SelectTrigger><SelectValue placeholder="— Create personal org —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Create personal org —</SelectItem>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>Cancel</Button>
            <Button
              disabled={createUserMut.isPending || !createUserEmail}
              onClick={() => createUserMut.mutate({ email: createUserEmail, name: createUserName, role: createUserRole, orgId: createUserOrgId })}>
              {createUserMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete User Confirmation Dialog ─── */}
      <Dialog open={deleteUserOpen} onOpenChange={open => { setDeleteUserOpen(open); if (!open) { setDeleteUserTarget(null); setDeleteUserConfirmEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><UserX className="h-4 w-4" /> Permanently Delete User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {deleteUserTarget && (
              <>
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-1">
                  <p className="font-semibold">{profileMap.get(deleteUserTarget)?.full_name || profileMap.get(deleteUserTarget)?.email}</p>
                  <p className="text-muted-foreground text-xs">{profileMap.get(deleteUserTarget)?.email}</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  This will permanently delete their account, all scenarios, roles, assignments, and audit entries. If they are the only member of their org, that org is also deleted. <strong>This cannot be undone.</strong>
                </p>
                <div className="space-y-2">
                  <Label>Type the user's email to confirm</Label>
                  <Input
                    value={deleteUserConfirmEmail}
                    onChange={e => setDeleteUserConfirmEmail(e.target.value)}
                    placeholder={profileMap.get(deleteUserTarget)?.email ?? ''}
                    className="font-mono text-sm"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={
                deleteUserMut.isPending ||
                !deleteUserTarget ||
                deleteUserConfirmEmail !== profileMap.get(deleteUserTarget ?? '')?.email
              }
              onClick={() => deleteUserTarget && deleteUserMut.mutate(deleteUserTarget)}>
              {deleteUserMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Deactivate / Delete an organization (20260929000002) ─── */}
      {orgAction && (
        <BranchActionDialog mode={orgAction.mode} root={hierarchy?.root ?? orgAction.org} org={orgAction.org}
          onClose={() => setOrgAction(null)}
          onDone={() => { afterDeactivation(); if (orgAction.mode === 'delete' && selectedOrgId === orgAction.org.id) setSelectedOrgId(null); }} />
      )}

      {/* ─── Deactivate a person ─── */}
      <Dialog open={!!deactivateUserTarget} onOpenChange={o => { if (!o) { setDeactivateUserTarget(null); setDeactivateUserConfirm(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserX className="h-4 w-4" /> Deactivate {profileMap.get(deactivateUserTarget ?? '')?.full_name || profileMap.get(deactivateUserTarget ?? '')?.email || 'user'}?</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              They're signed out and gone from every screen right away. Their clients stay with them, out of sight. Bring them back any time from the Deactivated list; after {ARCHIVE_YEARS_LABEL} the nightly purge wipes it.
            </p>
            <div className="space-y-2">
              <Label>Type the user's email to confirm</Label>
              <Input value={deactivateUserConfirm} onChange={e => setDeactivateUserConfirm(e.target.value)}
                placeholder={profileMap.get(deactivateUserTarget ?? '')?.email ?? ''} className="font-mono text-sm" autoComplete="off" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateUserTarget(null)}>Cancel</Button>
            <Button variant="destructive"
              disabled={deactivateUserMut.isPending || !deactivateUserTarget
                || deactivateUserConfirm.trim().toLowerCase() !== (profileMap.get(deactivateUserTarget ?? '')?.email ?? '').trim().toLowerCase()}
              onClick={() => deactivateUserTarget && deactivateUserMut.mutate(deactivateUserTarget)}>
              {deactivateUserMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserX className="h-4 w-4 mr-1" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
